import { useState, useEffect } from "react";
import axios from "axios";
import Login from "./Login";
import VoiceAssistant from "./VoiceAssistant";

axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// Feature flag: AI/voice assistant. Set to true to re-enable (component & backend code are kept intact).
const AI_ASSISTANT_ENABLED = false;

// Bild (z.B. Screenshot) verkleinern und als komprimierte JPEG-data-URL zurückgeben,
// damit die Anhänge klein bleiben (längste Kante max. 1600px, Qualität 0.8).
function fileToCompressedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
      img.onload = () => {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = Math.min(MAX / width, MAX / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const translations = {
  de: {
    title: "Reiseplanung",
    logout: "Abmelden",
    yourData: "Ihre Daten",
    namePlaceholder: "Max Mustermann",
    emailOptional: "Email (optional)",
    emailPlaceholder: "max@beispiel.de",
    checkinLabel: "Reisebeginn:",
    checkoutLabel: "Reiseende (optional):",
    tripEndOpen: "offen",
    hotelDatesTitle: "Hotel-Daten",
    hotelDatesHint: "Vorbefüllt aus den Reisedaten – bei Bedarf anpassen (z.B. Anreise am Vorabend).",
    hotelCheckinLabel: "Hotel Check-in: *",
    hotelCheckoutLabel: "Hotel Check-out: *",
    hotelPeriodLabel: "Hotel:",
    onSiteTitle: "Vor Ort am Arbeitsplatz (optional)",
    onSiteHint: "Wann wirst du am Zielort/Arbeitsplatz erwartet? Hilft beim passenden Buchen.",
    onSiteFromLabel: "Vor Ort von:",
    onSiteToLabel: "Vor Ort bis:",
    onSitePeriodLabel: "Vor Ort:",
    you: "Sie:",
    aiAssistant: "KI-Assistent:",
    summaryTitle: "✅ Zusammenfassung Ihrer Anforderungen",
    details: "📋 Details",
    adminInfoBtn: "ℹ️ Infos",
    adminInfoTitle: "Infos / Reservierungen",
    adminInfoEmpty: "Noch keine Infos hinterlegt.",
    route: "Strecke:",
    period: "Reisezeitraum:",
    periodTo: "bis",
    flightLabel: "Flug:",
    yes: "Ja",
    no: "Nein",
    outbound: "Hinflug (früheste Abfahrt):",
    returnFlight: "Rückflug (früheste Abfahrt):",
    accommodationLabel: "Unterkunft:",
    carRentalLabel: "Mietwagen:",
    searchTitle: "🔍 Jetzt selbst suchen",
    searchDesc: "Nutzen Sie diese Links, um passende Angebote zu finden:",
    searchHotels: "🏨 Hotels auf Booking.com suchen",
    searchFlights: "✈️ Flüge auf Idealo vergleichen",
    searchCar: "🚗 Mietwagen bei Sixt buchen",
    notesTitle: "📝 Ihre Notizen / Gefundene Angebote",
    notesDesc: "Nichts gefunden? Oder möchten Sie Links zu gefundenen Angeboten einfügen? Nutzen Sie dieses Feld für Ihre Notizen:",
    notesPlaceholder: "z.B.: Nichts passendes gefunden\n\noder:\n\nHotel: https://booking.com/...\nFlug: https://idealo.de/...\nMietwagen: Nicht verfügbar",
    submitBtn: "📤 Anfrage jetzt übermitteln",
    submitHint: "Ihre Anforderungen werden an das Admin-Team gesendet",
    editSubmitBtn: "💾 Änderungen speichern",
    editSubmitHint: "Sie können gesendete Anfragen bearbeiten, solange sie noch nicht abgeschlossen sind.",
    submitted: "✅ Anfrage erfolgreich übermittelt!",
    requestUpdated: "✅ Anfrage erfolgreich aktualisiert!",
    adminNote: "Ihr Admin-Team wird sich um die Buchung kümmern.",
    newRequest: "➕ Neue Anfrage stellen",
    inputPlaceholder: "Ihre Antwort eingeben...",
    send: "Senden",
    alertName: "Bitte geben Sie Ihren Namen ein!",
    alertPlan: "Bitte schließen Sie zuerst die Reiseplanung ab!",
    alertSuccess: "✅ Ihre Anfrage wurde erfolgreich übermittelt!",
    alertSendError: "Fehler beim Senden der Anfrage!",
    alertDates: "Bitte mindestens den Reisebeginn angeben!",
    alertHotelDates: "Bitte für die Unterkunft Hotel Check-in und Check-out angeben – beides ist für eine Buchung erforderlich.",
    alertBackend: "Fehler beim Kontaktieren des Backends!",
    tabPlanner: "✈️ Reiseplanung",
    tabAccount: "👤 Konto",
    tabRequests: "📋 Meine Anfragen",
    accountTitle: "Konto",
    language: "Sprache",
    noRequests: "Noch keine Anfragen gesendet.",
    requestsTitle: "Meine Anfragen",
    reqStatus_pending: "Ausstehend",
    reqStatus_in_progress: "In Bearbeitung",
    reqStatus_confirmed: "Bestätigt",
    reqStatus_cancelled: "Storniert",
    reqStatus_draft: "Entwurf",
    reqStatusBy: "Zuständig:",
    // Planer (PM)
    planForEmployeesBtn: "Für Mitarbeiter planen",
    planForSelfBtn: "Eigene Reise planen",
    composeBanner: "Planer-Modus: Du erstellst einen Reise-Entwurf für Mitarbeiter.",
    recipientsTitle: "Empfänger (Mitarbeiter)",
    recipientsDesc: "Wähle Mitarbeiter aus der Liste oder gib eine E-Mail-Adresse ein.",
    recipientSelectPlaceholder: "— Mitarbeiter wählen —",
    recipientAddBtn: "Hinzufügen",
    sendToEmployeesBtn: "An Mitarbeiter senden",
    alertNoRecipients: "Bitte mindestens einen Empfänger hinzufügen!",
    draftsSent: "Entwurf an die Mitarbeiter gesendet.",
    plannedByLabel: "Geplant von",
    fillDraftBtn: "📝 Ausfüllen & absenden",
    draftHint: "Dieser Reise-Entwurf wurde für dich vorbereitet. Bitte ergänze die Angaben und sende ihn ab.",
    draftsToComplete: "Offene Entwürfe – bitte ausfüllen",
    draftsToCompleteDesc: "Ein Planer hat diese Reise(n) für dich vorbereitet. Bitte ergänze die Angaben und sende sie ab.",
    cancelDraftBtn: "Bearbeitung abbrechen",
    reqRoute: "Strecke",
    reqDate: "Gesendet",
    reqCheckin: "Reisebeginn",
    reqCheckout: "Reiseende",
    messagesTitle: "💬 Nachrichten",
    noMessages: "Noch keine Nachrichten.",
    msgPlaceholder: "Nachricht schreiben...",
    msgAdmin: "Admin",
    msgYou: "Sie",
    attachImage: "Bild anhängen (oder Screenshot einfügen)",
    removeImage: "Bild entfernen",
    imgError: "Bild konnte nicht verarbeitet werden.",
    close: "Schließen",
    formSectionTitle: "Reisedetails",
    businessTripNumberLabel: "Dienstreisenummer",
    projectNumberLabel: "Projektnummer",
    projectNumberPlaceholder: "z.B. P-12345",
    projectNumberHint: "Optional – falls bekannt. Kann auch später unter 'Meine Anfragen' ergänzt werden.",
    editRequestBtn: "✏️ Anfrage bearbeiten",
    projectNumberEdit: "Bearbeiten",
    projectNumberSave: "Speichern",
    projectNumberCancel: "Abbrechen",
    projectNumberNone: "—",
    fromCity: "Von (Stadt/Ort)",
    toCity: "Nach (Stadt/Ort)",
    flightNeeded: "Wird ein Flug benötigt?",
    departureAirport: "Abflughafen",
    arrivalAirport: "Ankunftsflughafen",
    luggageLabel: "Gepäck",
    withLuggage: "Handgepäck",
    withoutLuggage: "Aufzugebendes Gepäck",
    outboundTimeLabel: "Früheste Abfahrtszeit Hinflug",
    returnTimeLabel: "Früheste Abfahrtszeit Rückflug",
    accommodationNeeded: "Unterkunft / Hotel benötigt?",
    carRentalNeeded: "Mietwagen benötigt?",
    createPlan: "✅ Reiseplan erstellen",
    fillAllFields: "Bitte alle Pflichtfelder ausfüllen (Von, Nach, Flug, Unterkunft, Mietwagen)!",
    airportPlaceholder: "z.B. Hamburg Airport (HAM)",
    flightFixOrFlex: "Fixer oder flexibler Flug?",
    flightFix: "Fix (bestimmtes Datum/Zeit)",
    flightFlex: "Flexibel",
    accommodationType: "Art der Unterkunft",
    accommodationAny: "Egal",
    accommodationHotel: "Hotel",
    accommodationApartment: "Wohnung / Apartment",
    breakfastLabel: "Mit Frühstück?",
    nearWorkplaceLabel: "Muss die Unterkunft nah am Werk/Arbeitsort sein?",
    carKmLabel: "Wie viele Inklusivkilometer werden benötigt?",
    carKmPlaceholder: "z.B. 500",
    followUpBtn: "✈️ Folgeantrag erstellen",
    followUpBanner: "Folgeantrag zu:",
    flightTypeLabel: "Hinflug oder Hin- und Rückflug?",
    flightOneWay: "Nur Hinflug",
    flightRoundTrip: "Hin- und Rückflug",
    deleteRequest: "🗑️ Anfrage löschen",
    deleteConfirm: "Diese Anfrage wirklich löschen?",
    flightFlexNote: "⚠️ Nur wählen, wenn eine Stornierung wirklich realistisch ist – es fallen zusätzliche Gebühren an.",
    workplaceLabel: "Werk / Arbeitsort",
    workplacePlaceholder: "z.B. Werk Hamburg",
    additionalDriverLabel: "Zusätzlicher Fahrer benötigt?",
    additionalDriverCountLabel: "Wie viele Zusatzfahrer?",
    additionalDriverCountPlaceholder: "z.B. 1",
    companyCarLabel: "Firmenwagen benötigt?",
    companyCarParkingLabel: "Parkplatz benötigt?",
    companyCarPlateLabel: "Kennzeichen des Firmenwagens",
    companyCarPlatePlaceholder: "z.B. HH-AB 1234",
    companyCarParkingLocationLabel: "Wo wird der Parkplatz benötigt?",
    companyCarParkingLocationPlaceholder: "z.B. Werk Hamburg, Parkdeck 2",
  },
  en: {
    title: "Travel Planning",
    logout: "Log out",
    yourData: "Your Details",
    namePlaceholder: "John Doe",
    emailOptional: "Email (optional)",
    emailPlaceholder: "john@example.com",
    checkinLabel: "Trip start:",
    checkoutLabel: "Trip end (optional):",
    tripEndOpen: "open",
    hotelDatesTitle: "Hotel dates",
    hotelDatesHint: "Prefilled from the trip dates – adjust if needed (e.g. arriving the evening before).",
    hotelCheckinLabel: "Hotel check-in: *",
    hotelCheckoutLabel: "Hotel check-out: *",
    hotelPeriodLabel: "Hotel:",
    onSiteTitle: "On-site at workplace (optional)",
    onSiteHint: "When are you expected at the destination/workplace? Helps with accurate booking.",
    onSiteFromLabel: "On-site from:",
    onSiteToLabel: "On-site to:",
    onSitePeriodLabel: "On-site:",
    you: "You:",
    aiAssistant: "AI Assistant:",
    summaryTitle: "✅ Summary of Your Requirements",
    details: "📋 Details",
    adminInfoBtn: "ℹ️ Info",
    adminInfoTitle: "Info / Reservations",
    adminInfoEmpty: "No info provided yet.",
    route: "Route:",
    period: "Trip period:",
    periodTo: "to",
    flightLabel: "Flight:",
    yes: "Yes",
    no: "No",
    outbound: "Outbound (earliest departure):",
    returnFlight: "Return (earliest departure):",
    accommodationLabel: "Accommodation:",
    carRentalLabel: "Car Rental:",
    searchTitle: "🔍 Search Now",
    searchDesc: "Use these links to find suitable offers:",
    searchHotels: "🏨 Search Hotels on Booking.com",
    searchFlights: "✈️ Compare Flights on Idealo",
    searchCar: "🚗 Book Car Rental at Sixt",
    notesTitle: "📝 Your Notes / Found Offers",
    notesDesc: "Nothing found? Or would you like to add links to found offers? Use this field for your notes:",
    notesPlaceholder: "e.g.: Nothing suitable found\n\nor:\n\nHotel: https://booking.com/...\nFlight: https://idealo.de/...",
    submitBtn: "📤 Submit Request Now",
    submitHint: "Your requirements will be sent to the admin team",
    editSubmitBtn: "💾 Save Changes",
    editSubmitHint: "You can edit submitted requests as long as they are not finalized yet.",
    submitted: "✅ Request successfully submitted!",
    requestUpdated: "✅ Request successfully updated!",
    adminNote: "Your admin team will take care of the booking.",
    newRequest: "➕ New Request",
    inputPlaceholder: "Enter your reply...",
    send: "Send",
    alertName: "Please enter your name!",
    alertPlan: "Please complete the travel planning first!",
    alertSuccess: "✅ Your request was successfully submitted!",
    alertSendError: "Error sending the request!",
    alertDates: "Please enter at least the trip start date!",
    alertHotelDates: "Please enter both hotel check-in and check-out for the accommodation – both are required to book.",
    alertBackend: "Error contacting the backend!",
    tabPlanner: "✈️ Travel Planner",
    tabAccount: "👤 Account",
    tabRequests: "📋 My Requests",
    accountTitle: "Account",
    language: "Language",
    noRequests: "No requests sent yet.",
    requestsTitle: "My Requests",
    reqStatus_pending: "Pending",
    reqStatus_in_progress: "In Progress",
    reqStatus_confirmed: "Confirmed",
    reqStatus_cancelled: "Cancelled",
    reqStatus_draft: "Draft",
    reqStatusBy: "Managed by:",
    // Planner (PM)
    planForEmployeesBtn: "Plan for employees",
    planForSelfBtn: "Plan my own trip",
    composeBanner: "Planner mode: you are creating a travel draft for employees.",
    recipientsTitle: "Recipients (employees)",
    recipientsDesc: "Pick employees from the list or enter an email address.",
    recipientSelectPlaceholder: "— Select employee —",
    recipientAddBtn: "Add",
    sendToEmployeesBtn: "Send to employees",
    alertNoRecipients: "Please add at least one recipient!",
    draftsSent: "Draft sent to the employees.",
    plannedByLabel: "Planned by",
    fillDraftBtn: "📝 Complete & submit",
    draftHint: "This travel draft was prepared for you. Please complete the details and submit it.",
    draftsToComplete: "Open drafts – please complete",
    draftsToCompleteDesc: "A planner prepared these trip(s) for you. Please complete the details and submit them.",
    cancelDraftBtn: "Cancel editing",
    reqRoute: "Route",
    reqDate: "Sent",
    reqCheckin: "Trip start",
    reqCheckout: "Trip end",
    messagesTitle: "💬 Messages",
    noMessages: "No messages yet.",
    msgPlaceholder: "Write a message...",
    msgAdmin: "Admin",
    msgYou: "You",
    attachImage: "Attach image (or paste a screenshot)",
    removeImage: "Remove image",
    imgError: "Image could not be processed.",
    close: "Close",
    formSectionTitle: "Travel Details",
    businessTripNumberLabel: "Business Trip Number",
    projectNumberLabel: "Project Number",
    projectNumberPlaceholder: "e.g. P-12345",
    projectNumberHint: "Optional – if known. Can also be added later under 'My Requests'.",
    editRequestBtn: "✏️ Edit Request",
    projectNumberEdit: "Edit",
    projectNumberSave: "Save",
    projectNumberCancel: "Cancel",
    projectNumberNone: "—",
    fromCity: "From (City/Location)",
    toCity: "To (City/Location)",
    flightNeeded: "Is a flight needed?",
    departureAirport: "Departure Airport",
    arrivalAirport: "Arrival Airport",
    luggageLabel: "Luggage",
    withLuggage: "Hand luggage",
    withoutLuggage: "Checked luggage",
    outboundTimeLabel: "Earliest outbound departure",
    returnTimeLabel: "Earliest return departure",
    accommodationNeeded: "Accommodation / Hotel needed?",
    carRentalNeeded: "Car rental needed?",
    createPlan: "✅ Create Travel Plan",
    fillAllFields: "Please fill in all required fields (From, To, Flight, Accommodation, Car Rental)!",
    airportPlaceholder: "e.g. Hamburg Airport (HAM)",
    flightFixOrFlex: "Fixed or flexible flight?",
    flightFix: "Fixed",
    flightFlex: "Flexible",
    accommodationType: "Type of accommodation",
    accommodationAny: "Any",
    accommodationHotel: "Hotel",
    accommodationApartment: "Apartment",
    breakfastLabel: "With breakfast?",
    nearWorkplaceLabel: "Does the accommodation need to be near the workplace/factory?",
    carKmLabel: "How many inclusive kilometers are needed?",
    carKmPlaceholder: "e.g. 500",
    followUpBtn: "✈️ Create Follow-up Request",
    followUpBanner: "Follow-up to:",
    flightTypeLabel: "One-way or round-trip?",
    flightOneWay: "One-way only",
    flightRoundTrip: "Round-trip",
    deleteRequest: "🗑️ Delete Request",
    deleteConfirm: "Really delete this request?",
    flightFlexNote: "⚠️ Only choose if cancellation is actually realistic – additional fees apply.",
    workplaceLabel: "Workplace / Factory",
    workplacePlaceholder: "e.g. Hamburg Factory",
    additionalDriverLabel: "Additional driver needed?",
    additionalDriverCountLabel: "How many additional drivers?",
    additionalDriverCountPlaceholder: "e.g. 1",
    companyCarLabel: "Company car needed?",
    companyCarParkingLabel: "Parking spot needed?",
    companyCarPlateLabel: "Company car license plate",
    companyCarPlatePlaceholder: "e.g. HH-AB 1234",
    companyCarParkingLocationLabel: "Where is the parking spot needed?",
    companyCarParkingLocationPlaceholder: "e.g. Hamburg factory, parking deck 2",
  },
  pl: {
    title: "Planowanie Podróży",
    logout: "Wyloguj",
    yourData: "Twoje Dane",
    namePlaceholder: "Jan Kowalski",
    emailOptional: "Email (opcjonalnie)",
    emailPlaceholder: "jan@przyklad.pl",
    checkinLabel: "Początek podróży:",
    checkoutLabel: "Koniec podróży (opcjonalnie):",
    tripEndOpen: "otwarty",
    hotelDatesTitle: "Daty hotelu",
    hotelDatesHint: "Wstępnie wypełnione z dat podróży – dostosuj w razie potrzeby (np. przyjazd dzień wcześniej).",
    hotelCheckinLabel: "Zameldowanie w hotelu: *",
    hotelCheckoutLabel: "Wymeldowanie z hotelu: *",
    hotelPeriodLabel: "Hotel:",
    onSiteTitle: "Na miejscu w pracy (opcjonalnie)",
    onSiteHint: "Kiedy jesteś oczekiwany w miejscu docelowym/pracy? Pomaga w trafnej rezerwacji.",
    onSiteFromLabel: "Na miejscu od:",
    onSiteToLabel: "Na miejscu do:",
    onSitePeriodLabel: "Na miejscu:",
    you: "Ty:",
    aiAssistant: "Asystent AI:",
    summaryTitle: "✅ Podsumowanie Twoich Wymagań",
    details: "📋 Szczegóły",
    adminInfoBtn: "ℹ️ Informacje",
    adminInfoTitle: "Informacje / Rezerwacje",
    adminInfoEmpty: "Brak informacji.",
    route: "Trasa:",
    period: "Okres podróży:",
    periodTo: "do",
    flightLabel: "Lot:",
    yes: "Tak",
    no: "Nie",
    outbound: "Lot tam (najwcześniejszy wylot):",
    returnFlight: "Lot powrotny (najwcześniejszy wylot):",
    accommodationLabel: "Zakwaterowanie:",
    carRentalLabel: "Wynajem samochodu:",
    searchTitle: "🔍 Szukaj teraz",
    searchDesc: "Skorzystaj z tych linków, aby znaleźć odpowiednie oferty:",
    searchHotels: "🏨 Szukaj hoteli na Booking.com",
    searchFlights: "✈️ Porównaj loty na Idealo",
    searchCar: "🚗 Zarezerwuj samochód w Sixt",
    notesTitle: "📝 Twoje Notatki / Znalezione Oferty",
    notesDesc: "Nic nie znalazłeś? Lub chcesz dodać linki do znalezionych ofert? Użyj tego pola na notatki:",
    notesPlaceholder: "np.: Nic odpowiedniego nie znaleziono\n\nlub:\n\nHotel: https://booking.com/...\nLot: https://idealo.de/...\nSamochód: Niedostępny",
    submitBtn: "📤 Wyślij Zapytanie",
    submitHint: "Twoje wymagania zostaną wysłane do zespołu administratora",
    editSubmitBtn: "💾 Zapisz zmiany",
    editSubmitHint: "Możesz edytować wysłane zapytania, dopóki nie są jeszcze zamknięte.",
    submitted: "✅ Zapytanie wysłane pomyślnie!",
    requestUpdated: "✅ Zapytanie zostało zaktualizowane pomyślnie!",
    adminNote: "Twój zespół administratora zajmie się rezerwacją.",
    newRequest: "➕ Nowe zapytanie",
    inputPlaceholder: "Wpisz swoją odpowiedź...",
    send: "Wyślij",
    alertName: "Proszę podać swoje imię!",
    alertPlan: "Proszę najpierw ukończyć planowanie podróży!",
    alertSuccess: "✅ Twoje zapytanie zostało wysłane pomyślnie!",
    alertSendError: "Błąd podczas wysyłania zapytania!",
    alertDates: "Proszę podać przynajmniej datę początku podróży!",
    alertHotelDates: "Proszę podać datę zameldowania i wymeldowania z hotelu – obie są wymagane do rezerwacji.",
    alertBackend: "Błąd podczas łączenia z serwerem!",
    tabPlanner: "✈️ Planowanie",
    tabAccount: "👤 Konto",
    tabRequests: "📋 Moje Zapytania",
    accountTitle: "Konto",
    language: "Język",
    noRequests: "Nie wysłano jeszcze żadnych zapytań.",
    requestsTitle: "Moje Zapytania",
    reqStatus_pending: "Oczekujące",
    reqStatus_in_progress: "W trakcie",
    reqStatus_confirmed: "Potwierdzone",
    reqStatus_cancelled: "Anulowane",
    reqStatus_draft: "Szkic",
    reqStatusBy: "Obsługuje:",
    // Planista (PM)
    planForEmployeesBtn: "Zaplanuj dla pracowników",
    planForSelfBtn: "Zaplanuj własną podróż",
    composeBanner: "Tryb planisty: tworzysz szkic podróży dla pracowników.",
    recipientsTitle: "Odbiorcy (pracownicy)",
    recipientsDesc: "Wybierz pracowników z listy lub wpisz adres e-mail.",
    recipientSelectPlaceholder: "— Wybierz pracownika —",
    recipientAddBtn: "Dodaj",
    sendToEmployeesBtn: "Wyślij do pracowników",
    alertNoRecipients: "Dodaj co najmniej jednego odbiorcę!",
    draftsSent: "Szkic wysłany do pracowników.",
    plannedByLabel: "Zaplanowane przez",
    fillDraftBtn: "📝 Uzupełnij i wyślij",
    draftHint: "Ten szkic podróży został przygotowany dla Ciebie. Uzupełnij dane i wyślij go.",
    draftsToComplete: "Otwarte szkice – uzupełnij",
    draftsToCompleteDesc: "Planista przygotował dla Ciebie tę podróż / te podróże. Uzupełnij dane i wyślij.",
    cancelDraftBtn: "Anuluj edycję",
    reqRoute: "Trasa",
    reqDate: "Wysłano",
    reqCheckin: "Początek podróży",
    reqCheckout: "Koniec podróży",
    messagesTitle: "💬 Wiadomości",
    noMessages: "Brak wiadomości.",
    msgPlaceholder: "Napisz wiadomość...",
    msgAdmin: "Admin",
    msgYou: "Ty",
    attachImage: "Dołącz obraz (lub wklej zrzut ekranu)",
    removeImage: "Usuń obraz",
    imgError: "Nie udało się przetworzyć obrazu.",
    close: "Zamknij",
    formSectionTitle: "Szczegóły Podróży",
    businessTripNumberLabel: "Numer podróży służbowej",
    projectNumberLabel: "Numer projektu",
    projectNumberPlaceholder: "np. P-12345",
    projectNumberHint: "Opcjonalnie – jeśli znany. Można też dodać później w sekcji 'Moje wnioski'.",
    editRequestBtn: "✏️ Edytuj zapytanie",
    projectNumberEdit: "Edytuj",
    projectNumberSave: "Zapisz",
    projectNumberCancel: "Anuluj",
    projectNumberNone: "—",
    fromCity: "Z (Miasto/Miejsce)",
    toCity: "Do (Miasto/Miejsce)",
    flightNeeded: "Czy potrzebny jest lot?",
    departureAirport: "Lotnisko Odlotu",
    arrivalAirport: "Lotnisko Przylotu",
    luggageLabel: "Bagaż",
    withLuggage: "Bagaż podręczny",
    withoutLuggage: "Bagaż rejestrowany",
    outboundTimeLabel: "Najwcześniejszy wylot (tam)",
    returnTimeLabel: "Najwcześniejszy wylot (powrót)",
    accommodationNeeded: "Czy potrzebne zakwaterowanie / hotel?",
    carRentalNeeded: "Czy potrzebny wynajem samochodu?",
    createPlan: "✅ Utwórz Plan Podróży",
    fillAllFields: "Proszę wypełnić wszystkie wymagane pola (Z, Do, Lot, Zakwaterowanie, Wynajem)!",
    airportPlaceholder: "np. Hamburg Airport (HAM)",
    flightFixOrFlex: "Stały czy elastyczny lot?",
    flightFix: "Stały (określona data/godzina)",
    flightFlex: "Elastyczny",
    accommodationType: "Rodzaj zakwaterowania",
    accommodationAny: "Dowolne",
    accommodationHotel: "Hotel",
    accommodationApartment: "Mieszkanie / Apartament",
    breakfastLabel: "Ze śniadaniem?",
    nearWorkplaceLabel: "Czy zakwaterowanie musi być blisko zakładu pracy?",
    carKmLabel: "Ile kilometrów w cenie jest potrzebnych?",
    carKmPlaceholder: "np. 500",
    followUpBtn: "✈️ Utwórz zapytanie kontynuacyjne",
    followUpBanner: "Kontynuacja:",
    flightTypeLabel: "W jedną stronę czy tam i z powrotem?",
    flightOneWay: "Tylko w jedną stronę",
    flightRoundTrip: "Tam i z powrotem",
    deleteRequest: "🗑️ Usuń zapytanie",
    deleteConfirm: "Naprawdę usunąć to zapytanie?",
    flightFlexNote: "⚠️ Wybierz tylko, jeśli anulowanie jest naprawdę realistyczne – obowiązują dodatkowe opłaty.",
    workplaceLabel: "Zakład / Miejsce pracy",
    workplacePlaceholder: "np. Zakład Hamburg",
    additionalDriverLabel: "Potrzebny dodatkowy kierowca?",
    additionalDriverCountLabel: "Ilu dodatkowych kierowców?",
    additionalDriverCountPlaceholder: "np. 1",
    companyCarLabel: "Potrzebny samochód służbowy?",
    companyCarParkingLabel: "Potrzebne miejsce parkingowe?",
    companyCarPlateLabel: "Numer rejestracyjny samochodu służbowego",
    companyCarPlatePlaceholder: "np. HH-AB 1234",
    companyCarParkingLocationLabel: "Gdzie potrzebne jest miejsce parkingowe?",
    companyCarParkingLocationPlaceholder: "np. Zakład Hamburg, poziom parkingowy 2",
  },
};

// Wandelt Freitext in JSX um und macht enthaltene URLs anklickbar (Reservierungslinks).
function renderInfoText(text) {
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1565c0", wordBreak: "break-all" }}
      >
        {part}
      </a>
    ) : (
      part
    )
  );
}

// Deep-Link aus einer Benachrichtigungs-Mail (?request=<id>&open=messages) einmalig auslesen.
// Der Wert wandert in den sessionStorage, weil der MSAL-Login-Redirect die URL austauscht —
// nach der Anmeldung landet man sonst wieder ohne Parameter auf der Startseite.
function readDeepLink() {
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("request");
    if (id) {
      sessionStorage.setItem("deepLinkRequest", JSON.stringify({ id, open: params.get("open") || "" }));
      params.delete("request");
      params.delete("open");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    const stored = sessionStorage.getItem("deepLinkRequest");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function App() {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userToken, setUserToken] = useState(() => localStorage.getItem("userToken") || "");
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "de");
  const t = translations[lang];

  const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token || userToken}` } });

  const toggleLang = (e) => {
    const next = e.target.value;
    localStorage.setItem("lang", next);
    setLang(next);
  };

  const [checkinInput, setCheckinInput] = useState(""); // Reisebeginn (Pflicht)
  const [checkoutInput, setCheckoutInput] = useState(""); // Reiseende (optional, leer = offene Rückreise/One-Way)
  const [hotelCheckinInput, setHotelCheckinInput] = useState(""); // Hotel Check-in (kann von Reisedaten abweichen)
  const [hotelCheckoutInput, setHotelCheckoutInput] = useState(""); // Hotel Check-out
  const [onSiteFromInput, setOnSiteFromInput] = useState(""); // Vor Ort am Arbeitsplatz von (optional)
  const [onSiteToInput, setOnSiteToInput] = useState(""); // Vor Ort am Arbeitsplatz bis (optional)
  const [finalPlan, setFinalPlan] = useState(null); // Finale Reiseplanung
  // Fragebogen-Felder
  const [formFrom, setFormFrom] = useState("");
  const [formTo, setFormTo] = useState("");
  const [formFlightNeeded, setFormFlightNeeded] = useState("");
  const [formDepartureAirport, setFormDepartureAirport] = useState("");
  const [formArrivalAirport, setFormArrivalAirport] = useState("");
  const [formLuggage, setFormLuggage] = useState("");
  const [formOutboundTime, setFormOutboundTime] = useState("");
  const [formReturnTime, setFormReturnTime] = useState("");
  const [formAccommodation, setFormAccommodation] = useState("");
  const [formAccommodationType, setFormAccommodationType] = useState(""); // hotel | apartment
  const [formBreakfast, setFormBreakfast] = useState(""); // yes | no
  const [formNearWorkplace, setFormNearWorkplace] = useState(""); // yes | no
  const [formCarRental, setFormCarRental] = useState("");
  const [formCarKm, setFormCarKm] = useState(""); // inklusivkilometer
  const [formFlightFlex, setFormFlightFlex] = useState(""); // fix | flex
  const [formFlightType, setFormFlightType] = useState(""); // oneway | roundtrip
  const [formWorkplace, setFormWorkplace] = useState("");
  const [formAdditionalDriver, setFormAdditionalDriver] = useState(""); // yes | no
  const [formAdditionalDriverCount, setFormAdditionalDriverCount] = useState("");
  const [formCompanyCar, setFormCompanyCar] = useState(""); // yes | no
  const [formCompanyCarParking, setFormCompanyCarParking] = useState(""); // yes | no
  const [formCompanyCarPlate, setFormCompanyCarPlate] = useState("");
  const [formCompanyCarParkingLocation, setFormCompanyCarParkingLocation] = useState("");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const [userNotes, setUserNotes] = useState(""); // Benutzer-Notizen/Links
  const [projectNumber, setProjectNumber] = useState(""); // Projektnummer beim Erstellen (optional)
  const [editingProjectId, setEditingProjectId] = useState(null); // Anfrage-ID, deren Projektnummer gerade bearbeitet wird
  const [projectNumberDraft, setProjectNumberDraft] = useState(""); // Zwischenwert beim Bearbeiten
  const [activeTab, setActiveTab] = useState("planner");
  const [myRequests, setMyRequests] = useState([]);
  const [myRequestsLoaded, setMyRequestsLoaded] = useState(false); // erster Abruf durch, damit "nicht gefunden" erkennbar ist
  const [deepLink, setDeepLink] = useState(readDeepLink); // aus einer Benachrichtigungs-Mail geöffnete Anfrage
  const [highlightReqId, setHighlightReqId] = useState(null); // kurzzeitige Hervorhebung nach dem Sprung
  const [expandedReqId, setExpandedReqId] = useState(null);
  const [infoOpenReqId, setInfoOpenReqId] = useState(null); // Anfrage, deren Admin-Infos gerade aufgeklappt sind
  const [reqMessages, setReqMessages] = useState({});
  const [reqMsgInput, setReqMsgInput] = useState("");
  const [reqMsgImages, setReqMsgImages] = useState([]); // ausstehende Bild-Anhänge (data-URLs) vor dem Senden
  const [reqImgBusy, setReqImgBusy] = useState(false); // während Bilder komprimiert werden
  const [lightboxSrc, setLightboxSrc] = useState(null); // Bild in einem In-App-Dialog groß anzeigen
  const [linkedRequest, setLinkedRequest] = useState(null); // Folgeantrag-Referenz
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState(null);
  // Planer (PM): draftMode = null (eigene Reise) | "compose" (Planer erstellt Entwurf) | { id } (Mitarbeiter füllt Entwurf aus)
  const [draftMode, setDraftMode] = useState(null);
  const [recipients, setRecipients] = useState([]); // [{ userId?, name?, email }]
  const [recipientEmailInput, setRecipientEmailInput] = useState("");
  const [knownUsers, setKnownUsers] = useState([]); // Dropdown-Quelle für Empfänger

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) { setAuthLoading(false); return; }
    axios.get("/api/profile", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setLoggedInUser(res.data.user);
        setUserToken(token);
        setUserName(res.data.user.name || "");
        setUserEmail(res.data.user.email || "");
      })
      .catch(() => { localStorage.removeItem("userToken"); })
      .finally(() => setAuthLoading(false));
  }, []);

  const deleteRequest = async (reqId) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      await axios.delete(`/api/requests/${reqId}`, authHeaders(userToken));
      setMyRequests(prev => prev.filter(r => r.id !== reqId));
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      console.error("Fehler beim Löschen:", msg);
      alert("Fehler beim Löschen: " + msg);
    }
  };

  const saveProjectNumber = async (reqId) => {
    try {
      const value = projectNumberDraft.trim();
      await axios.patch(`/api/requests/${reqId}/project-number`, { projectNumber: value }, authHeaders(userToken));
      setMyRequests(prev => prev.map(r => r.id === reqId ? { ...r, projectNumber: value } : r));
      setEditingProjectId(null);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      console.error("Fehler beim Speichern der Projektnummer:", msg);
      alert("Fehler: " + msg);
    }
  };

  const fetchMyRequests = async (token) => {
    const t_ = token || userToken;
    if (!t_) return;
    try {
      const res = await axios.get("/api/requests/mine", authHeaders(t_));
      setMyRequests(res.data);
      setMyRequestsLoaded(true);
    } catch (err) {
      console.error("Fehler beim Laden der Anfragen:", err.response?.data?.error || err.message);
    }
  };

  useEffect(() => {
    if (activeTab === "requests") fetchMyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userToken]);

  // Beim Login einmalig laden, damit der Entwurf-Zähler (Tab-Badge) sofort stimmt
  useEffect(() => {
    if (loggedInUser && userToken) fetchMyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, userToken]);

  const fetchMessages = async (reqId) => {
    try {
      const res = await axios.get(`/api/requests/${reqId}/messages`, authHeaders());
      setReqMessages(prev => ({ ...prev, [reqId]: res.data }));
    } catch (err) {
      console.error("Fehler beim Laden der Nachrichten:", err);
    }
  };

  // Bild-Dateien (aus Dateiauswahl oder Zwischenablage) komprimieren und zu den Anhängen hinzufügen
  const addReqImageFiles = async (files) => {
    const imageFiles = Array.from(files || []).filter(f => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    setReqImgBusy(true);
    try {
      const dataUrls = await Promise.all(imageFiles.map(fileToCompressedDataUrl));
      setReqMsgImages(prev => [...prev, ...dataUrls].slice(0, 10));
    } catch (err) {
      console.error("Bild konnte nicht verarbeitet werden:", err);
      alert(t.imgError);
    } finally {
      setReqImgBusy(false);
    }
  };

  const handleReqPaste = (e) => {
    const files = Array.from(e.clipboardData?.items || [])
      .filter(it => it.type.startsWith("image/"))
      .map(it => it.getAsFile())
      .filter(Boolean);
    if (files.length) {
      e.preventDefault();
      addReqImageFiles(files);
    }
  };

  const sendUserMessage = async (reqId) => {
    if (!reqMsgInput.trim() && reqMsgImages.length === 0) return;
    try {
      await axios.post(`/api/requests/${reqId}/messages`, {
        text: reqMsgInput.trim(),
        images: reqMsgImages,
        sender: "user"
      }, authHeaders());
      setReqMsgInput("");
      setReqMsgImages([]);
      fetchMessages(reqId);
    } catch (err) {
      console.error("Fehler beim Senden:", err);
      alert(t.alertSendError + " " + (err.response?.data?.error || err.message));
    }
  };

  useEffect(() => {
    if (!expandedReqId) return;
    fetchMessages(expandedReqId);
    const interval = setInterval(() => fetchMessages(expandedReqId), 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedReqId]);

  // Deep-Link aus der Mail anwenden: Anfragen-Tab öffnen und den Chat der Anfrage aufklappen
  useEffect(() => {
    if (!deepLink || !loggedInUser || !myRequestsLoaded) return;
    const req = myRequests.find(r => r.id === deepLink.id);
    setActiveTab("requests");
    if (req) {
      // Entwürfe haben keinen Nachrichten-Bereich — dort nur zur Karte springen
      if (req.status !== "draft" && deepLink.open === "messages") {
        setExpandedReqId(req.id);
        setReqMsgInput("");
        setReqMsgImages([]);
      }
      setHighlightReqId(req.id);
    }
    // Auch bei gelöschter/unbekannter Anfrage verwerfen, sonst greift der Link bei jedem Render erneut
    sessionStorage.removeItem("deepLinkRequest");
    setDeepLink(null);
  }, [deepLink, loggedInUser, myRequests, myRequestsLoaded]);

  // Hervorgehobene Anfrage in den Blick scrollen und die Markierung danach wieder lösen
  useEffect(() => {
    if (!highlightReqId) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(`req-${highlightReqId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const timeout = setTimeout(() => setHighlightReqId(null), 2500);
    return () => { cancelAnimationFrame(frame); clearTimeout(timeout); };
  }, [highlightReqId]);

  // Lightbox mit Escape-Taste schließen
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = e => { if (e.key === "Escape") setLightboxSrc(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxSrc]);

  const handleLogin = (user, token) => {
    setLoggedInUser(user);
    setUserName(user.name);
    setUserEmail(user.email);
    if (token) {
      localStorage.setItem("userToken", token);
      setUserToken(token);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post("/api/logout", {});
    } catch (_) {}
    localStorage.removeItem("userToken");
    setUserToken("");
    setLoggedInUser(null);
  };

  if (authLoading) return null;

  if (!loggedInUser) {
    return <Login onLogin={handleLogin} lang={lang} onChangeLang={toggleLang} />;
  }

  const parseDate = (input) => {
    return input; // date input already returns YYYY-MM-DD
  };

  const submitRequest = async () => {
    if (!userName) {
      alert(t.alertName);
      return;
    }
    
    if (!finalPlan) {
      alert(t.alertPlan);
      return;
    }

    try {
      const checkin = parseDate(checkinInput);
      const checkout = parseDate(checkoutInput);

      await axios.post("/api/requests", {
        userName,
        userEmail,
        checkin,
        checkout,
        projectNumber,
        finalPlan: {
          ...finalPlan,
          userNotes
        },
        linkedToId: linkedRequest?.id || null,
      }, authHeaders());

      setRequestSent(true);
      alert(t.alertSuccess);
      await fetchMyRequests();
      setActiveTab("requests");
    } catch (err) {
      console.error(err);
      alert(t.alertSendError);
    }
  };

  const saveEditedRequest = async () => {
    if (!editingRequestId) return;
    if (!userName) {
      alert(t.alertName);
      return;
    }

    if (!finalPlan) {
      alert(t.alertPlan);
      return;
    }

    try {
      await axios.patch(`/api/requests/${editingRequestId}`, {
        userName,
        userEmail,
        checkin: parseDate(checkinInput),
        checkout: parseDate(checkoutInput),
        projectNumber,
        finalPlan: {
          ...finalPlan,
          userNotes
        },
      }, authHeaders());

      setRequestSent(true);
      alert(t.requestUpdated);
      setEditingRequestId(null);
      await fetchMyRequests();
      setActiveTab("requests");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || t.alertSendError);
    }
  };

  const buildPlanFromForm = () => {
    if (!formFrom || !formTo || !formFlightNeeded || !formAccommodation || !formCarRental) {
      alert(t.fillAllFields);
      return;
    }
    if (!checkinInput) {
      alert(t.alertDates);
      return;
    }

    const checkin = parseDate(checkinInput);
    const checkout = parseDate(checkoutInput); // kann leer sein (offene Rückreise / One-Way)

    const flightNeeded = formFlightNeeded === "yes";
    const accommodationNeeded = formAccommodation === "yes";
    const carRentalNeeded = formCarRental === "yes";

    // Hotel-Daten übernehmen Reisedaten, falls nicht abweichend gesetzt
    const hotelCheckin = accommodationNeeded ? (hotelCheckinInput || checkin) : "";
    const hotelCheckout = accommodationNeeded ? (hotelCheckoutInput || checkout) : "";
    const onSiteFrom = onSiteFromInput || "";
    const onSiteTo = onSiteToInput || "";

    // Eine Unterkunft braucht Check-in UND Check-out (sonst nicht buchbar)
    if (accommodationNeeded && (!hotelCheckin || !hotelCheckout)) {
      alert(t.alertHotelDates);
      return;
    }

    const luggageText = formLuggage === "with"
      ? t.withLuggage
      : formLuggage === "without"
        ? t.withoutLuggage
        : "";

    const outboundStr = formOutboundTime ? `ab ${formOutboundTime}` : "";
    const returnStr = formFlightType === "roundtrip" && formReturnTime ? `ab ${formReturnTime}` : "";
    const isRoundTrip = formFlightType === "roundtrip";

    const summaryParts = [];
    if (lang === "de") {
      summaryParts.push(`Geschäftsreise von ${formFrom} nach ${formTo}, ${checkout ? `${checkin} bis ${checkout}` : `ab ${checkin} (Rückreise offen)`}.`);
      if (flightNeeded) {
        let flightDesc = `Flug von ${formDepartureAirport || formFrom} nach ${formArrivalAirport || formTo}`;
        flightDesc += ` (${isRoundTrip ? "Hin- und Rückflug" : "Nur Hinflug"})`;
        if (luggageText) flightDesc += `, ${luggageText}`;
        if (formFlightFlex) flightDesc += `, ${formFlightFlex === "fix" ? "fix" : "flexibel"}`;
        if (outboundStr) flightDesc += `, Hinflug ${outboundStr}`;
        if (returnStr) flightDesc += `, Rückflug ${returnStr}`;
        summaryParts.push(flightDesc + ".");
      } else {
        summaryParts.push("Kein Flug.");
      }
      if (accommodationNeeded) {
        let accDesc = `Unterkunft: ${formAccommodationType === "hotel" ? "Hotel" : formAccommodationType === "apartment" ? "Wohnung/Apartment" : "Ja"}`;
        if (formAccommodationType === "hotel" && formBreakfast) accDesc += `, Frühstück: ${formBreakfast === "yes" ? "Ja" : "Nein"}`;
        if (formNearWorkplace) accDesc += `, nah am Werk: ${formNearWorkplace === "yes" ? `Ja${formWorkplace ? ` (${formWorkplace})` : ""}` : "Nein"}`;
        summaryParts.push(accDesc + ".");
      } else {
        summaryParts.push("Keine Unterkunft.");
      }
      let carDesc = `Mietwagen: ${carRentalNeeded ? "Ja" : "Nein"}`;
      if (carRentalNeeded && formCarKm) carDesc += ` (${formCarKm} km inklusive)`;
      if (carRentalNeeded && formAdditionalDriver) carDesc += `, Zusatzfahrer: ${formAdditionalDriver === "yes" ? `Ja${formAdditionalDriverCount ? ` (${formAdditionalDriverCount})` : ""}` : "Nein"}`;
      summaryParts.push(carDesc + ".");
      if (formCompanyCar) {
        let ccDesc = `Firmenwagen: ${formCompanyCar === "yes" ? "Ja" : "Nein"}`;
        if (formCompanyCar === "yes" && formCompanyCarPlate) ccDesc += ` (${formCompanyCarPlate})`;
        if (formCompanyCar === "yes" && formCompanyCarParking) ccDesc += `, Parkplatz: ${formCompanyCarParking === "yes" ? "Ja" : "Nein"}`;
        if (formCompanyCar === "yes" && formCompanyCarParking === "yes" && formCompanyCarParkingLocation) ccDesc += ` (${formCompanyCarParkingLocation})`;
        summaryParts.push(ccDesc + ".");
      }
    } else if (lang === "pl") {
      summaryParts.push(`Podróż służbowa z ${formFrom} do ${formTo}, ${checkout ? `${checkin} do ${checkout}` : `od ${checkin} (powrót otwarty)`}.`);
      if (flightNeeded) {
        let flightDesc = `Lot z ${formDepartureAirport || formFrom} do ${formArrivalAirport || formTo}`;
        flightDesc += ` (${isRoundTrip ? "tam i z powrotem" : "tylko w jedną stronę"})`;
        if (luggageText) flightDesc += `, ${luggageText}`;
        if (formFlightFlex) flightDesc += `, ${formFlightFlex === "fix" ? "stały" : "elastyczny"}`;
        if (formOutboundTime) flightDesc += `, wylot od ${formOutboundTime}`;
        if (returnStr) flightDesc += `, powrót od ${formReturnTime}`;
        summaryParts.push(flightDesc + ".");
      } else {
        summaryParts.push("Bez lotu.");
      }
      if (accommodationNeeded) {
        let accDesc = `Zakwaterowanie: ${formAccommodationType === "hotel" ? "Hotel" : formAccommodationType === "apartment" ? "Apartament" : "Tak"}`;
        if (formAccommodationType === "hotel" && formBreakfast) accDesc += `, śniadanie: ${formBreakfast === "yes" ? "Tak" : "Nie"}`;
        if (formNearWorkplace) accDesc += `, blisko zakładu: ${formNearWorkplace === "yes" ? `Tak${formWorkplace ? ` (${formWorkplace})` : ""}` : "Nie"}`;
        summaryParts.push(accDesc + ".");
      } else {
        summaryParts.push("Bez zakwaterowania.");
      }
      let carDesc = `Wynajem: ${carRentalNeeded ? "Tak" : "Nie"}`;
      if (carRentalNeeded && formCarKm) carDesc += ` (${formCarKm} km w cenie)`;
      if (carRentalNeeded && formAdditionalDriver) carDesc += `, dodatkowy kierowca: ${formAdditionalDriver === "yes" ? `Tak${formAdditionalDriverCount ? ` (${formAdditionalDriverCount})` : ""}` : "Nie"}`;
      summaryParts.push(carDesc + ".");
      if (formCompanyCar) {
        let ccDesc = `Samochód służbowy: ${formCompanyCar === "yes" ? "Tak" : "Nie"}`;
        if (formCompanyCar === "yes" && formCompanyCarPlate) ccDesc += ` (${formCompanyCarPlate})`;
        if (formCompanyCar === "yes" && formCompanyCarParking) ccDesc += `, miejsce parkingowe: ${formCompanyCarParking === "yes" ? "Tak" : "Nie"}`;
        if (formCompanyCar === "yes" && formCompanyCarParking === "yes" && formCompanyCarParkingLocation) ccDesc += ` (${formCompanyCarParkingLocation})`;
        summaryParts.push(ccDesc + ".");
      }
    } else {
      summaryParts.push(`Business trip from ${formFrom} to ${formTo}, ${checkout ? `${checkin} to ${checkout}` : `from ${checkin} (return open)`}.`);
      if (flightNeeded) {
        let flightDesc = `Flight from ${formDepartureAirport || formFrom} to ${formArrivalAirport || formTo}`;
        flightDesc += ` (${isRoundTrip ? "round-trip" : "one-way"})`;
        if (luggageText) flightDesc += `, ${luggageText}`;
        if (formFlightFlex) flightDesc += `, ${formFlightFlex === "fix" ? "fixed" : "flexible"}`;
        if (formOutboundTime) flightDesc += `, outbound from ${formOutboundTime}`;
        if (returnStr) flightDesc += `, return from ${formReturnTime}`;
        summaryParts.push(flightDesc + ".");
      } else {
        summaryParts.push("No flight.");
      }
      if (accommodationNeeded) {
        let accDesc = `Accommodation: ${formAccommodationType === "hotel" ? "Hotel" : formAccommodationType === "apartment" ? "Apartment" : "Yes"}`;
        if (formAccommodationType === "hotel" && formBreakfast) accDesc += `, breakfast: ${formBreakfast === "yes" ? "Yes" : "No"}`;
        if (formNearWorkplace) accDesc += `, near workplace: ${formNearWorkplace === "yes" ? `Yes${formWorkplace ? ` (${formWorkplace})` : ""}` : "No"}`;
        summaryParts.push(accDesc + ".");
      } else {
        summaryParts.push("No accommodation.");
      }
      let carDesc = `Car rental: ${carRentalNeeded ? "Yes" : "No"}`;
      if (carRentalNeeded && formCarKm) carDesc += ` (${formCarKm} km included)`;
      if (carRentalNeeded && formAdditionalDriver) carDesc += `, additional driver: ${formAdditionalDriver === "yes" ? `Yes${formAdditionalDriverCount ? ` (${formAdditionalDriverCount})` : ""}` : "No"}`;
      summaryParts.push(carDesc + ".");
      if (formCompanyCar) {
        let ccDesc = `Company car: ${formCompanyCar === "yes" ? "Yes" : "No"}`;
        if (formCompanyCar === "yes" && formCompanyCarPlate) ccDesc += ` (${formCompanyCarPlate})`;
        if (formCompanyCar === "yes" && formCompanyCarParking) ccDesc += `, parking spot: ${formCompanyCarParking === "yes" ? "Yes" : "No"}`;
        if (formCompanyCar === "yes" && formCompanyCarParking === "yes" && formCompanyCarParkingLocation) ccDesc += ` (${formCompanyCarParkingLocation})`;
        summaryParts.push(ccDesc + ".");
      }
    }

    // Sprachneutrale Zusatzangaben (Hotel-Zeitraum, Vor-Ort), nur falls vorhanden
    if (accommodationNeeded && (hotelCheckin || hotelCheckout)) {
      summaryParts.push(`${t.hotelPeriodLabel} ${hotelCheckin || "?"}${hotelCheckout ? ` ${t.periodTo} ${hotelCheckout}` : ""}.`);
    }
    if (onSiteFrom || onSiteTo) {
      summaryParts.push(`${t.onSitePeriodLabel} ${onSiteFrom || "?"}${onSiteTo ? ` ${t.periodTo} ${onSiteTo}` : ""}.`);
    }

    setFinalPlan({
      requirements: {
        from: formFrom,
        to: formTo,
        dates: { checkin, checkout },
        onSite: (onSiteFrom || onSiteTo) ? { from: onSiteFrom || undefined, to: onSiteTo || undefined } : undefined,
        flight: {
          needed: flightNeeded,
          type: formFlightType || undefined,
          departureAirport: formDepartureAirport,
          arrivalAirport: formArrivalAirport,
          luggage: luggageText || undefined,
          flexibility: formFlightFlex || undefined,
          outboundEarliestDeparture: outboundStr || undefined,
          returnEarliestDeparture: returnStr || undefined,
        },
        accommodation: {
          needed: accommodationNeeded,
          checkIn: accommodationNeeded ? hotelCheckin : undefined,
          checkOut: accommodationNeeded ? (hotelCheckout || undefined) : undefined,
          type: formAccommodationType || undefined,
          breakfast: formAccommodationType === "hotel" && formBreakfast ? formBreakfast === "yes" : undefined,
          nearWorkplace: formNearWorkplace ? formNearWorkplace === "yes" : undefined,
          workplace: formNearWorkplace === "yes" && formWorkplace ? formWorkplace : undefined,
        },
        carRental: {
          needed: carRentalNeeded,
          inclusiveKm: carRentalNeeded && formCarKm ? Number(formCarKm) : undefined,
          additionalDriver: carRentalNeeded && formAdditionalDriver ? formAdditionalDriver === "yes" : undefined,
          additionalDriverCount: carRentalNeeded && formAdditionalDriver === "yes" && formAdditionalDriverCount ? Number(formAdditionalDriverCount) : undefined,
        },
        companyCar: formCompanyCar ? {
          needed: formCompanyCar === "yes",
          licensePlate: formCompanyCar === "yes" && formCompanyCarPlate ? formCompanyCarPlate : undefined,
          parkingSpot: formCompanyCar === "yes" && formCompanyCarParking ? formCompanyCarParking === "yes" : undefined,
          parkingLocation: formCompanyCar === "yes" && formCompanyCarParking === "yes" && formCompanyCarParkingLocation ? formCompanyCarParkingLocation : undefined,
        } : undefined,
      },
      summary: summaryParts.join(" "),
    });
  };

  const resetForm = () => {
    setFinalPlan(null);
    setRequestSent(false);
    setEditingRequestId(null);
    setCheckinInput("");
    setCheckoutInput("");
    setHotelCheckinInput("");
    setHotelCheckoutInput("");
    setOnSiteFromInput("");
    setOnSiteToInput("");
    setFormFrom("");
    setFormTo("");
    setFormFlightNeeded("");
    setFormDepartureAirport("");
    setFormArrivalAirport("");
    setFormLuggage("");
    setFormFlightFlex("");
    setFormOutboundTime("");
    setFormReturnTime("");
    setFormAccommodation("");
    setFormAccommodationType("");
    setFormBreakfast("");
    setFormNearWorkplace("");
    setFormCarRental("");
    setFormCarKm("");
    setFormWorkplace("");
    setFormAdditionalDriver("");
    setFormAdditionalDriverCount("");
    setFormCompanyCar("");
    setFormCompanyCarParking("");
    setFormCompanyCarPlate("");
    setFormCompanyCarParkingLocation("");
    setUserNotes("");
    setProjectNumber("");
    setLinkedRequest(null);
  };

  const followUpFrom = (req) => {
    const r = req.finalPlan?.requirements;
    resetForm();
    // Startpunkt = Zielort der Vorreise
    if (r?.to) setFormFrom(r.to);
    // Gepäck übernehmen
    if (r?.flight?.luggage) {
      const lug = r.flight.luggage.toLowerCase();
      if (lug.includes("hand") || lug.includes("mit") || lug.includes("with") || lug.includes("podr")) setFormLuggage("with");
      else if (lug.includes("aufzu") || lug.includes("checked") || lug.includes("ohne") || lug.includes("without") || lug.includes("rejestr")) setFormLuggage("without");
    }
    // Fix/Flex übernehmen
    if (r?.flight?.flexibility) setFormFlightFlex(r.flight.flexibility);
    // Unterkunftspräferenzen übernehmen
    if (r?.accommodation?.type) setFormAccommodationType(r.accommodation.type);
    if (r?.accommodation?.breakfast !== undefined) setFormBreakfast(r.accommodation.breakfast ? "yes" : "no");
    if (r?.accommodation?.nearWorkplace !== undefined) setFormNearWorkplace(r.accommodation.nearWorkplace ? "yes" : "no");
    // Mietwagen übernehmen
    if (r?.carRental?.needed !== undefined) setFormCarRental(r.carRental.needed ? "yes" : "no");
    if (r?.carRental?.inclusiveKm) setFormCarKm(String(r.carRental.inclusiveKm));
    if (r?.carRental?.additionalDriver !== undefined) setFormAdditionalDriver(r.carRental.additionalDriver ? "yes" : "no");
    if (r?.carRental?.additionalDriverCount) setFormAdditionalDriverCount(String(r.carRental.additionalDriverCount));
    if (r?.companyCar?.needed !== undefined) setFormCompanyCar(r.companyCar.needed ? "yes" : "no");
    if (r?.companyCar?.licensePlate) setFormCompanyCarPlate(r.companyCar.licensePlate);
    if (r?.companyCar?.parkingSpot !== undefined) setFormCompanyCarParking(r.companyCar.parkingSpot ? "yes" : "no");
    if (r?.companyCar?.parkingLocation) setFormCompanyCarParkingLocation(r.companyCar.parkingLocation);
    // Referenz merken für Banner
    setLinkedRequest(req);
    setActiveTab("planner");
  };

  const isPlanner = loggedInUser?.role === "planner" || loggedInUser?.role === "admin";

  // Entfernt Präfixe wie "ab "/"from " und liefert reines HH:MM für <input type="time">
  const stripTimePrefix = (val) => {
    if (!val) return "";
    const m = String(val).match(/(\d{1,2}:\d{2})/);
    return m ? m[1] : "";
  };

  // Inverse von buildPlanFromForm: befüllt die Formularfelder aus einem gespeicherten finalPlan
  const applyPlanToForm = (req) => {
    const r = req.finalPlan?.requirements || {};
    setFormFrom(r.from || "");
    setFormTo(r.to || "");
    setCheckinInput(req.checkin || r.dates?.checkin || "");
    setCheckoutInput(req.checkout || r.dates?.checkout || "");
    setOnSiteFromInput(r.onSite?.from || "");
    setOnSiteToInput(r.onSite?.to || "");
    // Flug
    if (r.flight) {
      setFormFlightNeeded(r.flight.needed ? "yes" : "no");
      if (r.flight.type) setFormFlightType(r.flight.type);
      setFormDepartureAirport(r.flight.departureAirport || "");
      setFormArrivalAirport(r.flight.arrivalAirport || "");
      if (r.flight.luggage) {
        const lug = r.flight.luggage.toLowerCase();
        if (lug.includes("hand") || lug.includes("mit") || lug.includes("with") || lug.includes("podr")) setFormLuggage("with");
        else if (lug.includes("aufzu") || lug.includes("checked") || lug.includes("ohne") || lug.includes("without") || lug.includes("rejestr")) setFormLuggage("without");
      }
      if (r.flight.flexibility) setFormFlightFlex(r.flight.flexibility);
      setFormOutboundTime(stripTimePrefix(r.flight.outboundEarliestDeparture));
      setFormReturnTime(stripTimePrefix(r.flight.returnEarliestDeparture));
    }
    // Unterkunft
    if (r.accommodation) {
      setFormAccommodation(r.accommodation.needed ? "yes" : "no");
      setHotelCheckinInput(r.accommodation.checkIn || "");
      setHotelCheckoutInput(r.accommodation.checkOut || "");
      if (r.accommodation.type) setFormAccommodationType(r.accommodation.type);
      if (r.accommodation.breakfast !== undefined) setFormBreakfast(r.accommodation.breakfast ? "yes" : "no");
      if (r.accommodation.nearWorkplace !== undefined) setFormNearWorkplace(r.accommodation.nearWorkplace ? "yes" : "no");
      if (r.accommodation.workplace) setFormWorkplace(r.accommodation.workplace);
    }
    // Mietwagen
    if (r.carRental) {
      setFormCarRental(r.carRental.needed ? "yes" : "no");
      if (r.carRental.inclusiveKm) setFormCarKm(String(r.carRental.inclusiveKm));
      if (r.carRental.additionalDriver !== undefined) setFormAdditionalDriver(r.carRental.additionalDriver ? "yes" : "no");
      if (r.carRental.additionalDriverCount) setFormAdditionalDriverCount(String(r.carRental.additionalDriverCount));
    }
    // Firmenwagen
    if (r.companyCar) {
      setFormCompanyCar(r.companyCar.needed ? "yes" : "no");
      if (r.companyCar.licensePlate) setFormCompanyCarPlate(r.companyCar.licensePlate);
      if (r.companyCar.parkingSpot !== undefined) setFormCompanyCarParking(r.companyCar.parkingSpot ? "yes" : "no");
      if (r.companyCar.parkingLocation) setFormCompanyCarParkingLocation(r.companyCar.parkingLocation);
    }
    if (req.finalPlan?.userNotes) setUserNotes(req.finalPlan.userNotes);
    if (req.projectNumber) setProjectNumber(req.projectNumber);
  };

  const fetchKnownUsers = async () => {
    try {
      const res = await axios.get("/api/planner/users", authHeaders());
      setKnownUsers(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Planer: Modus "Für Mitarbeiter planen" starten/beenden
  const startCompose = () => {
    resetForm();
    setRecipients([]);
    setRecipientEmailInput("");
    setDraftMode("compose");
    fetchKnownUsers();
    setActiveTab("planner");
  };
  const exitCompose = () => {
    resetForm();
    setRecipients([]);
    setDraftMode(null);
  };

  const addRecipientFromSelect = (id) => {
    const u = knownUsers.find((k) => k.id === id);
    if (!u || !u.email) return;
    if (recipients.some((r) => (r.email || "").toLowerCase() === u.email.toLowerCase())) return;
    setRecipients((prev) => [...prev, { userId: u.id, name: u.name, email: u.email }]);
  };
  const addRecipientFromEmail = () => {
    const email = recipientEmailInput.trim();
    if (!email) return;
    if (recipients.some((r) => (r.email || "").toLowerCase() === email.toLowerCase())) { setRecipientEmailInput(""); return; }
    setRecipients((prev) => [...prev, { email }]);
    setRecipientEmailInput("");
  };
  const removeRecipient = (email) =>
    setRecipients((prev) => prev.filter((r) => r.email !== email));

  // Planer: Entwürfe an die Empfänger senden
  const sendDrafts = async () => {
    if (!finalPlan) { alert(t.alertPlan); return; }
    if (recipients.length === 0) { alert(t.alertNoRecipients); return; }
    try {
      await axios.post("/api/planner/drafts", {
        recipients,
        checkin: parseDate(checkinInput),
        checkout: parseDate(checkoutInput),
        projectNumber,
        finalPlan: { ...finalPlan, userNotes },
      }, authHeaders());
      alert(t.draftsSent);
      exitCompose();
      setActiveTab("requests");
    } catch (err) {
      console.error(err);
      alert(t.alertSendError);
    }
  };

  // Mitarbeiter: empfangenen Entwurf öffnen und ins Formular laden
  const openDraft = (req) => {
    resetForm();
    applyPlanToForm(req);
    if (loggedInUser?.name && !userName) setUserName(loggedInUser.name);
    if (loggedInUser?.email && !userEmail) setUserEmail(loggedInUser.email);
    setDraftMode({ id: req.id });
    setActiveTab("planner");
  };

  const openRequestForEdit = (req) => {
    resetForm();
    applyPlanToForm(req);
    setUserName(req.userName || loggedInUser?.name || "");
    setUserEmail(req.userEmail || loggedInUser?.email || "");
    setEditingRequestId(req.id);
    setActiveTab("planner");
  };

  // Mitarbeiter: Entwurf-Bearbeitung abbrechen (Entwurf bleibt offen erhalten)
  const cancelDraftEdit = () => {
    resetForm();
    setDraftMode(null);
    setActiveTab("requests");
  };

  // Mitarbeiter: ausgefüllten Entwurf absenden → wird zur regulären Anfrage
  const submitDraft = async () => {
    if (!finalPlan) { alert(t.alertPlan); return; }
    try {
      await axios.post(`/api/requests/${draftMode.id}/submit-draft`, {
        checkin: parseDate(checkinInput),
        checkout: parseDate(checkoutInput),
        projectNumber,
        finalPlan: { ...finalPlan, userNotes },
      }, authHeaders());
      setRequestSent(true);
      alert(t.alertSuccess);
      setDraftMode(null);
      await fetchMyRequests();
      setActiveTab("requests");
    } catch (err) {
      console.error(err);
      alert(t.alertSendError);
    }
  };

  const statusColor = (s) =>
    s === "confirmed" ? "#2e7d32" : s === "cancelled" ? "#c62828" : s === "in progress" ? "#9c27b0" : s === "draft" ? "#607d8b" : "#e65100";
  const statusLabel = (s) =>
    t[`reqStatus_${s.replace(" ", "_")}`] || s;
  const canEditRequest = (req) => ["pending", "in progress"].includes(req.status);

  const draftCount = myRequests.filter(r => r.status === "draft").length;
  const tabs = [
    { key: "planner", label: t.tabPlanner },
    { key: "requests", label: draftCount > 0 ? `${t.tabRequests} (${draftCount})` : t.tabRequests },
    { key: "account", label: t.tabAccount },
  ];

  return (
    <div style={{ display: isMobile ? "block" : "flex", minHeight: "100vh", fontFamily: "inherit" }}>

      {/* ── Mobile fixed header ── */}
      {isMobile && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 56, zIndex: 50, backgroundColor: "#1a237e", color: "#fff", display: "flex", alignItems: "center", padding: "0 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
          <button onClick={() => setMenuOpen(true)} style={{ background: "none", border: "none", color: "#fff", fontSize: 26, cursor: "pointer", padding: "4px 10px 4px 0", lineHeight: 1 }} aria-label="Open menu">☰</button>
          <img src="/ZW_white.png" alt="Zwtrip Logo" style={{ height: 32 }} />
          <span style={{ marginLeft: "auto", fontSize: 13, opacity: 0.8 }}>{loggedInUser.name}</span>
        </div>
      )}

      {/* ── Mobile drawer overlay ── */}
      {isMobile && menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 100 }} />
          <div style={{ position: "fixed", top: 0, left: 0, width: 260, height: "100vh", backgroundColor: "#1a237e", color: "#fff", zIndex: 101, display: "flex", flexDirection: "column", boxShadow: "4px 0 20px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <img src="/ZW_white.png" alt="Zwtrip Logo" style={{ height: 36 }} />
              <button onClick={() => setMenuOpen(false)} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", lineHeight: 1 }} aria-label="Close menu">✕</button>
            </div>
            <div style={{ fontSize: 13, opacity: 0.7, padding: "12px 20px 0" }}>{loggedInUser.name}</div>
            <nav style={{ marginTop: 12, flex: 1 }}>
              {tabs.map(tab => (
                <button key={tab.key} onClick={() => { setActiveTab(tab.key); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "14px 20px", background: activeTab === tab.key ? "rgba(255,255,255,0.15)" : "transparent", color: "#fff", border: "none", borderLeft: activeTab === tab.key ? "4px solid #90caf9" : "4px solid transparent", fontSize: 15, fontWeight: activeTab === tab.key ? "bold" : "normal", cursor: "pointer" }}>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </>
      )}

      {/* ── Desktop sidebar ── */}
      {!isMobile && (
        <div style={{ width: 220, backgroundColor: "#1a237e", color: "#fff", display: "flex", flexDirection: "column", padding: "24px 0", flexShrink: 0 }}>
          <div style={{ padding: "0 20px 24px", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
            <img src="/ZW_white.png" alt="Zwtrip Logo" style={{ height: 40, display: "block", marginBottom: 6 }} />
            <div style={{ fontSize: 13, opacity: 0.7 }}>{loggedInUser.name}</div>
          </div>
          <nav style={{ marginTop: 16, flex: 1 }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 20px", background: activeTab === tab.key ? "rgba(255,255,255,0.15)" : "transparent", color: "#fff", border: "none", borderLeft: activeTab === tab.key ? "4px solid #90caf9" : "4px solid transparent", fontSize: 14, fontWeight: activeTab === tab.key ? "bold" : "normal", cursor: "pointer" }}>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* ── Main content ── */}
      <div style={{ flex: 1, padding: isMobile ? "72px 16px 24px" : 28, overflowY: isMobile ? "visible" : "auto", backgroundColor: "#f5f7fa" }}>

        {/* ═══════════════ ACCOUNT TAB ═══════════════ */}
        {activeTab === "account" && (
          <div style={{ maxWidth: 480 }}>
            <h2 style={{ marginTop: 0 }}>{t.accountTitle}</h2>

            <div style={{ background: "#fff", borderRadius: 10, padding: 24, marginBottom: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 2 }}>Name</div>
                <div style={{ fontSize: 16, fontWeight: "bold" }}>{loggedInUser.name}</div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 2 }}>Email</div>
                <div style={{ fontSize: 16 }}>{loggedInUser.email}</div>
              </div>

            </div>

            <div style={{ background: "#fff", borderRadius: 10, padding: 24, marginBottom: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>{t.language}</div>
              <select
                value={lang}
                onChange={toggleLang}
                style={{
                  padding: "8px 12px",
                  border: "2px solid #1565c0",
                  borderRadius: 6,
                  fontSize: 14,
                  color: "#1565c0",
                  fontWeight: "bold",
                  cursor: "pointer",
                  backgroundColor: "#fff"
                }}
              >
                <option value="de">🇩🇪 Deutsch</option>
                <option value="en">🇬🇧 English</option>
                <option value="pl">🇵🇱 Polski</option>
              </select>
            </div>

            <button
              onClick={handleLogout}
              style={{
                padding: "10px 24px",
                backgroundColor: "#ef5350",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 15,
                fontWeight: "bold",
                cursor: "pointer"
              }}
            >
              {t.logout}
            </button>
          </div>
        )}

        {/* ═══════════════ MY REQUESTS TAB ═══════════════ */}
        {activeTab === "requests" && (
          <div style={{ maxWidth: 700 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>{t.requestsTitle}</h2>
              <button
                onClick={() => fetchMyRequests()}
                style={{
                  padding: "6px 14px",
                  backgroundColor: "#1565c0",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13
                }}
              >
                ↻
              </button>
            </div>

            {/* Prominente Sektion: offene Entwürfe, die der Planer für dich vorbereitet hat */}
            {myRequests.some(r => r.status === "draft") && (
              <div style={{ background: "#fff8e1", border: "2px solid #ff9800", borderRadius: 12, padding: 18, marginBottom: 24, boxShadow: "0 2px 10px rgba(255,152,0,0.25)" }}>
                <h3 style={{ marginTop: 0, marginBottom: 6, color: "#e65100", display: "flex", alignItems: "center", gap: 8 }}>
                  📝 {t.draftsToComplete}
                  <span style={{ backgroundColor: "#e65100", color: "#fff", borderRadius: 12, fontSize: 13, padding: "1px 9px" }}>
                    {myRequests.filter(r => r.status === "draft").length}
                  </span>
                </h3>
                <p style={{ margin: "0 0 14px 0", fontSize: 13, color: "#8d6e00" }}>{t.draftsToCompleteDesc}</p>
                {myRequests.filter(r => r.status === "draft").map(req => (
                  <div key={req.id} id={`req-${req.id}`} style={{ background: "#fff", border: "1px solid #ffcc80", borderRadius: 8, padding: 14, marginBottom: 10, boxShadow: highlightReqId === req.id ? "0 0 0 3px #e65100" : "none", transition: "box-shadow 0.3s" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: "bold", fontSize: 15 }}>
                          {req.finalPlan?.requirements?.from && req.finalPlan?.requirements?.to
                            ? `${req.finalPlan.requirements.from} → ${req.finalPlan.requirements.to}`
                            : `#${req.id}`}
                        </div>
                        <div style={{ fontSize: 13, color: "#666", marginTop: 2 }}>
                          {req.checkin || "?"}{req.checkout ? ` → ${req.checkout}` : ""}
                        </div>
                        {req.plannedByName && (
                          <div style={{ fontSize: 12, color: "#607d8b", marginTop: 2 }}>
                            👤 {t.plannedByLabel}: {req.plannedByName}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          onClick={() => openDraft(req)}
                          style={{ padding: "8px 16px", backgroundColor: "#e65100", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: "bold" }}
                        >
                          {t.fillDraftBtn}
                        </button>
                        <button
                          onClick={() => deleteRequest(req.id)}
                          style={{ padding: "8px 12px", backgroundColor: "#fff5f5", color: "#c62828", border: "1px solid #c62828", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: "bold" }}
                        >
                          {t.deleteRequest}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {myRequests.filter(r => r.status !== "draft").length === 0 ? (
              myRequests.some(r => r.status === "draft") ? null : (
                <div style={{ background: "#fff", borderRadius: 10, padding: 32, textAlign: "center", color: "#999", boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
                  {t.noRequests}
                </div>
              )
            ) : (() => {
              // Build a chained order: chains with newest activity first,
              // while keeping follow-ups visually attached to their parent.
              // Entwürfe werden hier ausgeklammert — sie erscheinen in der prominenten Sektion oben.
              const visible = myRequests.filter(r => r.status !== "draft");
              const byId = {};
              visible.forEach(r => { byId[r.id] = r; });
              const roots = visible.filter(r => !r.linkedToId || !byId[r.linkedToId]);
              const followUps = visible.filter(r => r.linkedToId && byId[r.linkedToId]);
              const childrenByParent = {};
              followUps.forEach((f) => {
                if (!childrenByParent[f.linkedToId]) childrenByParent[f.linkedToId] = [];
                childrenByParent[f.linkedToId].push(f);
              });
              Object.values(childrenByParent).forEach(arr => arr.sort((a,b) => new Date(a.timestamp)-new Date(b.timestamp)));
              const ordered = [];
              const latestByNode = {};
              const getLatestTs = (id) => {
                if (latestByNode[id] !== undefined) return latestByNode[id];
                const selfTs = byId[id] ? new Date(byId[id].timestamp).getTime() : 0;
                const childTs = (childrenByParent[id] || []).reduce((maxTs, child) => {
                  const ts = getLatestTs(child.id);
                  return ts > maxTs ? ts : maxTs;
                }, 0);
                latestByNode[id] = selfTs > childTs ? selfTs : childTs;
                return latestByNode[id];
              };
              const insertChain = (req) => {
                ordered.push(req);
                (childrenByParent[req.id] || []).forEach(insertChain);
              };
              roots.slice().sort((a,b) => getLatestTs(b.id) - getLatestTs(a.id)).forEach(insertChain);
              // Any orphaned follow-ups (parent deleted) appended at end
              visible.filter(r => !ordered.includes(r)).forEach(r => ordered.push(r));

              return ordered.map((req, idx) => {
                const isFollowUp = req.linkedToId && byId[req.linkedToId];
                const prevReq = ordered[idx - 1];
                const isDirectChild = isFollowUp && prevReq && prevReq.id === req.linkedToId;
                const parentReq = isFollowUp ? byId[req.linkedToId] : null;
                return (
                <div key={req.id}>
                  {isFollowUp && (
                    <div style={{ display: "flex", alignItems: "stretch", marginLeft: 24, marginBottom: 0 }}>
                      <div style={{ width: 2, backgroundColor: "#90caf9", marginRight: 14, borderRadius: 2, minHeight: isDirectChild ? 20 : 20 }} />
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        color: "#1565c0",
                        fontWeight: "bold",
                        padding: "4px 10px",
                        backgroundColor: "#e3f2fd",
                        borderRadius: 12,
                        border: "1px solid #90caf9",
                        marginBottom: 4,
                        marginTop: 4,
                        alignSelf: "flex-start",
                      }}>
                        ↳ {t.followUpBanner} {parentReq ? `${parentReq.finalPlan?.requirements?.from} → ${parentReq.finalPlan?.requirements?.to} (${parentReq.checkin})` : `#${req.linkedToId}`}
                      </div>
                    </div>
                  )}
                <div id={`req-${req.id}`} style={{
                  background: "#fff",
                  borderRadius: 10,
                  padding: 20,
                  marginBottom: 14,
                  marginLeft: isFollowUp ? 24 : 0,
                  borderLeft: isFollowUp ? `4px solid #90caf9` : `4px solid ${statusColor(req.status)}`,
                  boxShadow: highlightReqId === req.id ? "0 0 0 3px #1565c0" : "0 1px 6px rgba(0,0,0,0.08)",
                  transition: "box-shadow 0.3s",
                  position: "relative",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div style={{ fontWeight: "bold", fontSize: 16 }}>
                      {req.finalPlan?.requirements?.from && req.finalPlan?.requirements?.to
                        ? `${req.finalPlan.requirements.from} → ${req.finalPlan.requirements.to}`
                        : `#${req.id}`}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                      <span style={{
                        padding: "3px 10px",
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: "bold",
                        backgroundColor: statusColor(req.status) + "22",
                        color: statusColor(req.status)
                      }}>
                        {statusLabel(req.status)}
                      </span>
                      {req.statusBy && req.status !== "pending" && (
                        <div style={{ fontSize: 11, color: "#9c27b0", fontWeight: "bold" }}>
                          👤 {req.statusBy}
                        </div>
                      )}
                      {req.plannedByName && (
                        <div style={{ fontSize: 11, color: "#607d8b", fontWeight: "bold" }}>
                          👤 {t.plannedByLabel}: {req.plannedByName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: 8, fontSize: 13, color: "#555" }}>
                    <div><span style={{ color: "#888" }}>{t.reqCheckin}:</span> {req.checkin}</div>
                    <div><span style={{ color: "#888" }}>{t.reqCheckout}:</span> {req.checkout || t.tripEndOpen}</div>
                    <div><span style={{ color: "#888" }}>{t.reqDate}:</span> {new Date(req.timestamp).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8, fontSize: 13, color: "#555", marginTop: 8 }}>
                    <div>
                      <span style={{ color: "#888" }}>{t.businessTripNumberLabel}:</span>{" "}
                      <strong>{req.businessTripNumber || t.projectNumberNone}</strong>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ color: "#888" }}>{t.projectNumberLabel}:</span>
                      {editingProjectId === req.id ? (
                        <>
                          <input
                            value={projectNumberDraft}
                            onChange={e => setProjectNumberDraft(e.target.value)}
                            placeholder={t.projectNumberPlaceholder}
                            style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid #ccc", fontSize: 13, width: 130 }}
                          />
                          <button
                            onClick={() => saveProjectNumber(req.id)}
                            style={{ padding: "2px 10px", backgroundColor: "#4caf50", color: "#fff", border: "none", borderRadius: 4, fontSize: 12, cursor: "pointer", fontWeight: "bold" }}
                          >{t.projectNumberSave}</button>
                          <button
                            onClick={() => setEditingProjectId(null)}
                            style={{ padding: "2px 10px", backgroundColor: "#eee", color: "#555", border: "none", borderRadius: 4, fontSize: 12, cursor: "pointer" }}
                          >{t.projectNumberCancel}</button>
                        </>
                      ) : (
                        <>
                          <strong>{req.projectNumber || t.projectNumberNone}</strong>
                          <button
                            onClick={() => { setEditingProjectId(req.id); setProjectNumberDraft(req.projectNumber || ""); }}
                            style={{ padding: "2px 10px", backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", borderRadius: 4, fontSize: 12, cursor: "pointer", fontWeight: "bold" }}
                          >{t.projectNumberEdit}</button>
                        </>
                      )}
                    </div>
                  </div>
                  {req.finalPlan?.summary && (
                    <div style={{ marginTop: 10, fontSize: 13, color: "#666", fontStyle: "italic", borderTop: "1px solid #eee", paddingTop: 10 }}>
                      {req.finalPlan.summary}
                    </div>
                  )}

                  {/* Messages section */}
                  <div style={{ marginTop: 10, borderTop: "1px solid #eee", paddingTop: 10 }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                      {req.status === "draft" && (
                        <button
                          onClick={() => openDraft(req)}
                          style={{
                            padding: "4px 12px",
                            backgroundColor: "#1565c0",
                            color: "#fff",
                            border: "1px solid #1565c0",
                            borderRadius: 14,
                            fontSize: 13,
                            cursor: "pointer",
                            fontWeight: "bold"
                          }}
                        >
                          {t.fillDraftBtn}
                        </button>
                      )}
                      {canEditRequest(req) && (
                      <button
                        onClick={() => openRequestForEdit(req)}
                        style={{
                          padding: "4px 12px",
                          backgroundColor: "#fff8e1",
                          color: "#ef6c00",
                          border: "1px solid #ef6c00",
                          borderRadius: 14,
                          fontSize: 13,
                          cursor: "pointer",
                          fontWeight: "bold"
                        }}
                      >
                        {t.editRequestBtn}
                      </button>
                      )}
                      {req.status !== "draft" && (
                      <button
                        onClick={() => {
                          setExpandedReqId(expandedReqId === req.id ? null : req.id);
                          setReqMsgInput("");
                          setReqMsgImages([]);
                        }}
                        style={{
                          padding: "4px 12px",
                          backgroundColor: expandedReqId === req.id ? "#1565c0" : "#e3f2fd",
                          color: expandedReqId === req.id ? "#fff" : "#1565c0",
                          border: "1px solid #1565c0",
                          borderRadius: 14,
                          fontSize: 13,
                          cursor: "pointer",
                          fontWeight: "bold"
                        }}
                      >
                        {t.messagesTitle}{reqMessages[req.id]?.length ? ` (${reqMessages[req.id].length})` : ""}
                      </button>
                      )}
                      {req.status !== "draft" && (
                      <button
                        onClick={() => setInfoOpenReqId(infoOpenReqId === req.id ? null : req.id)}
                        style={{
                          padding: "4px 12px",
                          backgroundColor: infoOpenReqId === req.id ? "#f57c00" : "#fff3e0",
                          color: infoOpenReqId === req.id ? "#fff" : "#e65100",
                          border: "1px solid #f57c00",
                          borderRadius: 14,
                          fontSize: 13,
                          cursor: "pointer",
                          fontWeight: "bold",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6
                        }}
                      >
                        {t.adminInfoBtn}
                        {req.adminInfo && req.adminInfo.trim() && (
                          <span style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: infoOpenReqId === req.id ? "#fff" : "#f57c00",
                            display: "inline-block"
                          }} />
                        )}
                      </button>
                      )}
                      {req.status !== "draft" && (
                      <button
                        onClick={() => followUpFrom(req)}
                        style={{
                          padding: "4px 12px",
                          backgroundColor: "#e8f5e9",
                          color: "#2e7d32",
                          border: "1px solid #2e7d32",
                          borderRadius: 14,
                          fontSize: 13,
                          cursor: "pointer",
                          fontWeight: "bold"
                        }}
                      >
                        {t.followUpBtn}
                      </button>
                      )}
                      <button
                        onClick={() => deleteRequest(req.id)}
                        style={{
                          padding: "4px 12px",
                          backgroundColor: "#fff5f5",
                          color: "#c62828",
                          border: "1px solid #c62828",
                          borderRadius: 14,
                          fontSize: 13,
                          cursor: "pointer",
                          fontWeight: "bold",
                          marginLeft: "auto"
                        }}
                      >
                        {t.deleteRequest}
                      </button>
                    </div>

                    {infoOpenReqId === req.id && (
                      <div style={{
                        marginTop: 10,
                        border: "1px solid #ffe0b2",
                        borderRadius: 8,
                        padding: 12,
                        backgroundColor: "#fff8f0"
                      }}>
                        <div style={{ fontSize: 13, fontWeight: "bold", color: "#e65100", marginBottom: 6 }}>
                          {t.adminInfoTitle}
                        </div>
                        {req.adminInfo && req.adminInfo.trim() ? (
                          <div style={{ whiteSpace: "pre-wrap", fontSize: 14, color: "#333", lineHeight: 1.5 }}>
                            {renderInfoText(req.adminInfo)}
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: "#999", fontStyle: "italic" }}>
                            {t.adminInfoEmpty}
                          </div>
                        )}
                      </div>
                    )}

                    {expandedReqId === req.id && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{
                          maxHeight: 200,
                          overflowY: "auto",
                          border: "1px solid #ddd",
                          borderRadius: 8,
                          padding: 8,
                          marginBottom: 8,
                          backgroundColor: "#fafafa"
                        }}>
                          {(!reqMessages[req.id] || reqMessages[req.id].length === 0) ? (
                            <div style={{ color: "#999", textAlign: "center", padding: 8, fontSize: 13 }}>{t.noMessages}</div>
                          ) : (
                            reqMessages[req.id].map(m => (
                              <div key={m.id} style={{
                                padding: 8,
                                marginBottom: 6,
                                borderRadius: 6,
                                backgroundColor: m.sender === "user" ? "#e3f2fd" : "#fff3e0",
                                borderLeft: `3px solid ${m.sender === "user" ? "#2196f3" : "#ff9800"}`
                              }}>
                                <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
                                  {m.sender === "user" ? t.msgYou : (m.senderName || t.msgAdmin)} · {new Date(m.timestamp).toLocaleTimeString()}
                                </div>
                                {m.text && <div style={{ fontSize: 14 }}>{m.text}</div>}
                                {m.images?.length > 0 && (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: m.text ? 6 : 0 }}>
                                    {m.images.map((src, i) => (
                                      <img key={i} src={src} alt="" onClick={() => setLightboxSrc(src)} style={{ maxHeight: 160, maxWidth: "100%", borderRadius: 6, border: "1px solid #ddd", cursor: "pointer", display: "block" }} />
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                        {reqMsgImages.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                            {reqMsgImages.map((src, i) => (
                              <div key={i} style={{ position: "relative" }}>
                                <img src={src} alt="" style={{ height: 60, width: 60, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }} />
                                <button
                                  onClick={() => setReqMsgImages(prev => prev.filter((_, j) => j !== i))}
                                  title={t.removeImage}
                                  style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, lineHeight: "16px", padding: 0, borderRadius: "50%", border: "none", backgroundColor: "#c62828", color: "#fff", fontSize: 12, cursor: "pointer" }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 8 }}>
                          <label
                            title={t.attachImage}
                            style={{ padding: "6px 10px", backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", borderRadius: 4, cursor: reqImgBusy ? "wait" : "pointer", fontSize: 16, display: "flex", alignItems: "center" }}
                          >
                            {reqImgBusy ? "⏳" : "📎"}
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              disabled={reqImgBusy}
                              style={{ display: "none" }}
                              onChange={e => { addReqImageFiles(e.target.files); e.target.value = ""; }}
                            />
                          </label>
                          <input
                            style={{ flex: 1, padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                            value={reqMsgInput}
                            onChange={e => setReqMsgInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && sendUserMessage(req.id)}
                            onPaste={handleReqPaste}
                            placeholder={t.msgPlaceholder}
                          />
                          <button
                            onClick={() => sendUserMessage(req.id)}
                            style={{ padding: "6px 14px", backgroundColor: "#1565c0", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14, fontWeight: "bold" }}
                          >
                            {t.send}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </div>
              );
              });
            })()}
          </div>
        )}

        {/* ═══════════════ PLANNER TAB ═══════════════ */}
        {activeTab === "planner" && (
          <div style={{ maxWidth: 600 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 0, gap: 8, flexWrap: "wrap" }}>
              <h2 style={{ marginTop: 0, marginBottom: 0 }}>{t.title}</h2>
              <div style={{ display: "flex", gap: 8 }}>
                {/* Planer-only: Umschalten zwischen eigener Reise und Entwurf für Mitarbeiter */}
                {isPlanner && !draftMode?.id && (
                  <button
                    onClick={draftMode === "compose" ? exitCompose : startCompose}
                    style={{
                      padding: "6px 14px",
                      backgroundColor: draftMode === "compose" ? "#1565c0" : "#e3f2fd",
                      color: draftMode === "compose" ? "#fff" : "#1565c0",
                      border: "1px solid #1565c0",
                      borderRadius: 6,
                      fontSize: 13,
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    {draftMode === "compose" ? t.planForSelfBtn : t.planForEmployeesBtn}
                  </button>
                )}
                <button
                  onClick={resetForm}
                  title="Reset form"
                  style={{
                    padding: "6px 14px",
                    backgroundColor: "#fff",
                    color: "#e53935",
                    border: "1px solid #e53935",
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  ↺ Reset
                </button>
              </div>
            </div>

            {/* Planer-Modus Banner */}
            {draftMode === "compose" && (
              <div style={{ backgroundColor: "#e3f2fd", border: "1px solid #1565c0", borderRadius: 8, padding: "10px 16px", margin: "12px 0", fontSize: 14, color: "#1565c0" }}>
                <strong>{t.composeBanner}</strong>
              </div>
            )}
            {/* Empfangener Entwurf Banner */}
            {draftMode?.id && (
              <div style={{ backgroundColor: "#eceff1", border: "1px solid #607d8b", borderRadius: 8, padding: "10px 16px", margin: "12px 0", fontSize: 14, color: "#455a64", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span>📝 {t.draftHint}</span>
                <button
                  onClick={cancelDraftEdit}
                  style={{ padding: "6px 14px", backgroundColor: "#fff", color: "#455a64", border: "1px solid #607d8b", borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: "bold", whiteSpace: "nowrap" }}
                >
                  ✕ {t.cancelDraftBtn}
                </button>
              </div>
            )}

            {AI_ASSISTANT_ENABLED && (
            <VoiceAssistant
              lang={lang}
              userToken={userToken}
              onFill={(f) => {
                const toHHMM = (val) => {
                  if (!val) return null;
                  let s = String(val).trim();
                  // Already HH:MM
                  if (/^\d{2}:\d{2}$/.test(s)) return s;
                  // HH:MM:SS → drop seconds
                  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
                  // Detect AM/PM from natural-language phrases before stripping
                  let forcedPeriod = "";
                  if (/\b(morning|morgens|in der früh|am morgen|früh)\b/i.test(s)) forcedPeriod = "am";
                  if (/\b(evening|abends|nachmittag|afternoon)\b/i.test(s)) forcedPeriod = "pm";
                  // Strip leading prepositions/prefixes: "ab 8", "from 8", "at 8", "um 8"
                  s = s.replace(/^(ab|from|von|at|um|depuis)\s+/i, "");
                  // Strip time-of-day word phrases
                  s = s.replace(/\s*(in the morning|in the evening|morgens|abends|am morgen|nachmittags|in der früh|früh)\s*/gi, "");
                  // Strip trailing units: "Uhr", "o'clock", "h" (but not "pm"/"am")
                  s = s.replace(/\s*(uhr|o'clock|clock)\s*$/i, "").trim();
                  // Match: "8", "8:00", "08:30", "8am", "8 am", "8:00 AM", "14:30"
                  const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
                  if (!m) return null;
                  let hours = parseInt(m[1], 10);
                  const minutes = parseInt(m[2] || "0", 10);
                  const period = (m[3] || forcedPeriod).toLowerCase();
                  if (period === "pm" && hours < 12) hours += 12;
                  if (period === "am" && hours === 12) hours = 0;
                  if (hours > 23 || minutes > 59) return null;
                  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
                };
                if (f.from) setFormFrom(f.from);
                if (f.to) setFormTo(f.to);
                if (f.checkin) setCheckinInput(f.checkin);
                if (f.checkout) setCheckoutInput(f.checkout);
                if (f.flightNeeded) setFormFlightNeeded(f.flightNeeded);
                if (f.flightType) setFormFlightType(f.flightType);
                if (f.departureAirport) setFormDepartureAirport(f.departureAirport);
                if (f.arrivalAirport) setFormArrivalAirport(f.arrivalAirport);
                if (f.luggage) setFormLuggage(f.luggage);
                if (f.flightFlex) setFormFlightFlex(f.flightFlex);
                const ot = toHHMM(f.outboundTime); if (ot) setFormOutboundTime(ot);
                const rt = toHHMM(f.returnTime); if (rt) setFormReturnTime(rt);
                if (f.accommodation) setFormAccommodation(f.accommodation);
                if (f.accommodationType) setFormAccommodationType(f.accommodationType);
                if (f.breakfast) setFormBreakfast(f.breakfast);
                if (f.nearWorkplace) setFormNearWorkplace(f.nearWorkplace);
                if (f.workplace) setFormWorkplace(f.workplace);
                if (f.carRental) setFormCarRental(f.carRental);
                if (f.carKm) setFormCarKm(f.carKm);
                if (f.additionalDriver) setFormAdditionalDriver(f.additionalDriver);
                if (f.additionalDriverCount) setFormAdditionalDriverCount(f.additionalDriverCount);
                if (f.companyCar) setFormCompanyCar(f.companyCar);
                if (f.companyCarParking) setFormCompanyCarParking(f.companyCarParking);
                if (f.companyCarPlate) setFormCompanyCarPlate(f.companyCarPlate);
                if (f.companyCarParkingLocation) setFormCompanyCarParkingLocation(f.companyCarParkingLocation);
              }}
            />
            )}

            {/* Folgeantrag-Banner */}
            {linkedRequest && (
              <div style={{
                backgroundColor: "#e8f5e9",
                border: "1px solid #2e7d32",
                borderRadius: 8,
                padding: "10px 16px",
                marginBottom: 16,
                fontSize: 14,
                color: "#2e7d32",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>
                  ✈️ <strong>{t.followUpBanner}</strong>{" "}
                  {linkedRequest.finalPlan?.requirements?.from} → {linkedRequest.finalPlan?.requirements?.to}
                  {" "}({linkedRequest.checkin})
                </span>
                <button
                  onClick={() => setLinkedRequest(null)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#2e7d32", fontSize: 16, fontWeight: "bold", padding: "0 4px" }}
                  title="Verknüpfung entfernen"
                >×</button>
              </div>
            )}

            {/* Benutzer-Informationen — im Planer-Compose-Modus ausgeblendet (Empfänger werden unten gewählt) */}
            {draftMode !== "compose" && (
            <div style={{
              border: "2px solid #2196f3",
              padding: 15,
              marginBottom: 15,
              borderRadius: 8,
              backgroundColor: "#fff"
            }}>
        <h3 style={{ marginTop: 0 }}>{t.yourData}</h3>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", marginBottom: 5, fontWeight: "bold" }}>Name *</label>
          <input
            style={{ padding: 8, width: "100%", borderRadius: 4, border: "1px solid #ccc" }}
            value={userName}
            onChange={e => setUserName(e.target.value)}
            placeholder={t.namePlaceholder}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 5, fontWeight: "bold" }}>{t.emailOptional}</label>
          <input
            style={{ padding: 8, width: "100%", borderRadius: 4, border: "1px solid #ccc" }}
            value={userEmail}
            onChange={e => setUserEmail(e.target.value)}
            placeholder={t.emailPlaceholder}
            type="email"
          />
        </div>
      </div>
            )}

            {/* Empfänger-Auswahl (nur Planer-Compose-Modus) — steht oben statt der Benutzer-Infos */}
            {draftMode === "compose" && (
            <div style={{
              border: "2px solid #1565c0",
              padding: 15,
              marginBottom: 15,
              borderRadius: 8,
              backgroundColor: "#fff"
            }}>
              <h3 style={{ marginTop: 0 }}>{t.recipientsTitle}</h3>
              <p style={{ fontSize: 14, color: "#666", marginBottom: 10 }}>{t.recipientsDesc}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                <select
                  defaultValue=""
                  onChange={e => { addRecipientFromSelect(e.target.value); e.target.value = ""; }}
                  style={{ flex: 1, minWidth: 200, padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}
                >
                  <option value="">{t.recipientSelectPlaceholder}</option>
                  {knownUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name}{u.email ? ` (${u.email})` : ""}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                <input
                  type="email"
                  value={recipientEmailInput}
                  onChange={e => setRecipientEmailInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addRecipientFromEmail(); } }}
                  placeholder={t.emailPlaceholder}
                  style={{ flex: 1, minWidth: 200, padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}
                />
                <button
                  onClick={addRecipientFromEmail}
                  style={{ padding: "8px 16px", backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #1565c0", borderRadius: 6, fontSize: 14, cursor: "pointer", fontWeight: "bold" }}
                >{t.recipientAddBtn}</button>
              </div>
              {recipients.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                  {recipients.map(r => (
                    <span key={r.email} style={{ display: "inline-flex", alignItems: "center", gap: 6, backgroundColor: "#e8f5e9", border: "1px solid #2e7d32", color: "#2e7d32", borderRadius: 14, padding: "3px 10px", fontSize: 13, fontWeight: "bold" }}>
                      {r.name || r.email}
                      <button onClick={() => removeRecipient(r.email)} style={{ background: "none", border: "none", cursor: "pointer", color: "#2e7d32", fontWeight: "bold", fontSize: 15, padding: 0, lineHeight: 1 }} title="Entfernen">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            )}

      {/* Reisedaten */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: 16, 
        marginBottom: 15, 
        borderRadius: 8,
        backgroundColor: "#fff"
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <div>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.checkinLabel}</label>
            <input
              type="date"
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, width: 180, cursor: "pointer" }}
              value={checkinInput}
              min={new Date().toISOString().split("T")[0]}
              onChange={e => {
                setCheckinInput(e.target.value);
                if (checkoutInput && e.target.value > checkoutInput) {
                  setCheckoutInput(e.target.value);
                }
              }}
              onClick={e => e.target.showPicker?.()}
            />
          </div>
          <div>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.checkoutLabel}</label>
            <input
              type="date"
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, width: 180, cursor: "pointer" }}
              value={checkoutInput}
              min={checkinInput || new Date().toISOString().split("T")[0]}
              onChange={e => setCheckoutInput(e.target.value)}
              onClick={e => e.target.showPicker?.()}
            />
          </div>
        </div>

        {/* Vor Ort am Arbeitsplatz (optional) */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #eee" }}>
          <div style={{ fontWeight: "bold", marginBottom: 2 }}>{t.onSiteTitle}</div>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>{t.onSiteHint}</div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div>
              <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.onSiteFromLabel}</label>
              <input
                type="date"
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, width: 180, cursor: "pointer" }}
                value={onSiteFromInput}
                onChange={e => setOnSiteFromInput(e.target.value)}
                onClick={e => e.target.showPicker?.()}
              />
            </div>
            <div>
              <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.onSiteToLabel}</label>
              <input
                type="date"
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, width: 180, cursor: "pointer" }}
                value={onSiteToInput}
                min={onSiteFromInput || undefined}
                onChange={e => setOnSiteToInput(e.target.value)}
                onClick={e => e.target.showPicker?.()}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fragebogen */}
      {!finalPlan && (
        <div style={{ background: "#fff", borderRadius: 10, padding: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>{t.formSectionTitle}</h3>

          {/* Von / Nach */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.fromCity} *</label>
              <input
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                value={formFrom}
                onChange={e => setFormFrom(e.target.value)}
                placeholder="Hamburg"
              />
            </div>
            <div>
              <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.toCity} *</label>
              <input
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                value={formTo}
                onChange={e => setFormTo(e.target.value)}
                placeholder="Gdansk"
              />
            </div>
          </div>

          {/* Flug benötigt? */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.flightNeeded} *</label>
            <div style={{ display: "flex", gap: 12 }}>
              {["yes", "no"].map(v => (
                <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formFlightNeeded === v ? "#1565c0" : "#ddd"}`, backgroundColor: formFlightNeeded === v ? "#e3f2fd" : "#fff" }}>
                  <input type="radio" name="flightNeeded" value={v} checked={formFlightNeeded === v} onChange={() => setFormFlightNeeded(v)} style={{ accentColor: "#1565c0" }} />
                  {v === "yes" ? t.yes : t.no}
                </label>
              ))}
            </div>
          </div>

          {/* Flug-Details (nur wenn Flug = Ja) */}
          {formFlightNeeded === "yes" && (
            <div style={{ padding: 16, backgroundColor: "#f0f4ff", borderRadius: 8, marginBottom: 16, border: "1px solid #c5cae9" }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.departureAirport}</label>
                  <input
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                    value={formDepartureAirport}
                    onChange={e => setFormDepartureAirport(e.target.value)}
                    placeholder={t.airportPlaceholder}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.arrivalAirport}</label>
                  <input
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                    value={formArrivalAirport}
                    onChange={e => setFormArrivalAirport(e.target.value)}
                    placeholder={t.airportPlaceholder}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.luggageLabel}</label>
                <div style={{ display: "flex", gap: 12 }}>
                  {["with", "without"].map(v => (
                    <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formLuggage === v ? "#1565c0" : "#ddd"}`, backgroundColor: formLuggage === v ? "#e3f2fd" : "#fff" }}>
                      <input type="radio" name="luggage" value={v} checked={formLuggage === v} onChange={() => setFormLuggage(v)} style={{ accentColor: "#1565c0" }} />
                      {v === "with" ? t.withLuggage : t.withoutLuggage}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.flightTypeLabel}</label>
                <div style={{ display: "flex", gap: 12 }}>
                  {["oneway", "roundtrip"].map(v => (
                    <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formFlightType === v ? "#1565c0" : "#ddd"}`, backgroundColor: formFlightType === v ? "#e3f2fd" : "#fff" }}>
                      <input type="radio" name="flightType" value={v} checked={formFlightType === v} onChange={() => setFormFlightType(v)} style={{ accentColor: "#1565c0" }} />
                      {v === "oneway" ? t.flightOneWay : t.flightRoundTrip}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: !isMobile && formFlightType === "roundtrip" ? "1fr 1fr" : "1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.outboundTimeLabel}</label>
                  <input
                    type="time"
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                    value={formOutboundTime}
                    onChange={e => setFormOutboundTime(e.target.value)}
                  />
                </div>
                {formFlightType === "roundtrip" && (
                  <div>
                    <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.returnTimeLabel}</label>
                    <input
                      type="time"
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                      value={formReturnTime}
                      onChange={e => setFormReturnTime(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Fix oder Flex */}
              <div style={{ marginTop: 12 }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.flightFixOrFlex}</label>
                <div style={{ display: "flex", gap: 12 }}>
                  {["fix", "flex"].map(v => (
                    <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formFlightFlex === v ? "#1565c0" : "#ddd"}`, backgroundColor: formFlightFlex === v ? "#e3f2fd" : "#fff" }}>
                      <input type="radio" name="flightFlex" value={v} checked={formFlightFlex === v} onChange={() => setFormFlightFlex(v)} style={{ accentColor: "#1565c0" }} />
                      {v === "fix" ? t.flightFix : t.flightFlex}
                    </label>
                  ))}
                </div>
                {formFlightFlex === "flex" && (
                  <div style={{ marginTop: 8, padding: "8px 12px", backgroundColor: "#fff8e1", borderRadius: 6, border: "1px solid #ffe082", fontSize: 13, color: "#7c5e00" }}>
                    {t.flightFlexNote}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Unterkunft */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.accommodationNeeded} *</label>
            <div style={{ display: "flex", gap: 12 }}>
              {["yes", "no"].map(v => (
                <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formAccommodation === v ? "#1565c0" : "#ddd"}`, backgroundColor: formAccommodation === v ? "#e3f2fd" : "#fff" }}>
                  <input type="radio" name="accommodation" value={v} checked={formAccommodation === v} onChange={() => {
                    setFormAccommodation(v);
                    // Hotel-Daten aus den Reisedaten vorbefüllen, sofern noch leer
                    if (v === "yes") {
                      if (!hotelCheckinInput && checkinInput) setHotelCheckinInput(checkinInput);
                      if (!hotelCheckoutInput && checkoutInput) setHotelCheckoutInput(checkoutInput);
                    }
                  }} style={{ accentColor: "#1565c0" }} />
                  {v === "yes" ? t.yes : t.no}
                </label>
              ))}
            </div>
          </div>

          {/* Unterkunft-Details (nur wenn Unterkunft = Ja) */}
          {formAccommodation === "yes" && (
            <div style={{ padding: 16, backgroundColor: "#f0fff4", borderRadius: 8, marginBottom: 16, border: "1px solid #a5d6a7" }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 2 }}>{t.hotelDatesTitle}</label>
                <div style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>{t.hotelDatesHint}</div>
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                  <div>
                    <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.hotelCheckinLabel}</label>
                    <input
                      type="date"
                      style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #a5d6a7", fontSize: 14, width: 180, cursor: "pointer" }}
                      value={hotelCheckinInput}
                      onChange={e => setHotelCheckinInput(e.target.value)}
                      onClick={e => e.target.showPicker?.()}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.hotelCheckoutLabel}</label>
                    <input
                      type="date"
                      style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #a5d6a7", fontSize: 14, width: 180, cursor: "pointer" }}
                      value={hotelCheckoutInput}
                      min={hotelCheckinInput || undefined}
                      onChange={e => setHotelCheckoutInput(e.target.value)}
                      onClick={e => e.target.showPicker?.()}
                    />
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.accommodationType}</label>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {["hotel", "apartment", "any"].map(v => (
                    <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formAccommodationType === v ? "#2e7d32" : "#ddd"}`, backgroundColor: formAccommodationType === v ? "#e8f5e9" : "#fff" }}>
                      <input type="radio" name="accommodationType" value={v} checked={formAccommodationType === v} onChange={() => setFormAccommodationType(v)} style={{ accentColor: "#2e7d32" }} />
                      {v === "hotel" ? t.accommodationHotel : v === "apartment" ? t.accommodationApartment : t.accommodationAny}
                    </label>
                  ))}
                </div>
              </div>

              {formAccommodationType === "hotel" && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.breakfastLabel}</label>
                  <div style={{ display: "flex", gap: 12 }}>
                    {["yes", "no"].map(v => (
                      <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formBreakfast === v ? "#2e7d32" : "#ddd"}`, backgroundColor: formBreakfast === v ? "#e8f5e9" : "#fff" }}>
                        <input type="radio" name="breakfast" value={v} checked={formBreakfast === v} onChange={() => setFormBreakfast(v)} style={{ accentColor: "#2e7d32" }} />
                        {v === "yes" ? t.yes : t.no}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.nearWorkplaceLabel}</label>
                <div style={{ display: "flex", gap: 12 }}>
                  {["yes", "no"].map(v => (
                    <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formNearWorkplace === v ? "#2e7d32" : "#ddd"}`, backgroundColor: formNearWorkplace === v ? "#e8f5e9" : "#fff" }}>
                      <input type="radio" name="nearWorkplace" value={v} checked={formNearWorkplace === v} onChange={() => setFormNearWorkplace(v)} style={{ accentColor: "#2e7d32" }} />
                      {v === "yes" ? t.yes : t.no}
                    </label>
                  ))}
                </div>
                {formNearWorkplace === "yes" && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.workplaceLabel}</label>
                    <input
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #a5d6a7", fontSize: 14, boxSizing: "border-box" }}
                      value={formWorkplace}
                      onChange={e => setFormWorkplace(e.target.value)}
                      placeholder={t.workplacePlaceholder}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mietwagen */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.carRentalNeeded} *</label>
            <div style={{ display: "flex", gap: 12 }}>
              {["yes", "no"].map(v => (
                <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formCarRental === v ? "#1565c0" : "#ddd"}`, backgroundColor: formCarRental === v ? "#e3f2fd" : "#fff" }}>
                  <input type="radio" name="carRental" value={v} checked={formCarRental === v} onChange={() => setFormCarRental(v)} style={{ accentColor: "#1565c0" }} />
                  {v === "yes" ? t.yes : t.no}
                </label>
              ))}
            </div>
          </div>

          {/* Mietwagen-Details (nur wenn Mietwagen = Ja) */}
          {formCarRental === "yes" && (
            <div style={{ padding: 16, backgroundColor: "#fff8e1", borderRadius: 8, marginBottom: 16, border: "1px solid #ffe082" }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.carKmLabel}</label>
                <input
                  type="number"
                  min="0"
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                  value={formCarKm}
                  onChange={e => setFormCarKm(e.target.value)}
                  placeholder={t.carKmPlaceholder}
                />
              </div>
              <div>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.additionalDriverLabel}</label>
                <div style={{ display: "flex", gap: 12 }}>
                  {["yes", "no"].map(v => (
                    <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formAdditionalDriver === v ? "#e65100" : "#ddd"}`, backgroundColor: formAdditionalDriver === v ? "#fff3e0" : "#fff" }}>
                      <input type="radio" name="additionalDriver" value={v} checked={formAdditionalDriver === v} onChange={() => setFormAdditionalDriver(v)} style={{ accentColor: "#e65100" }} />
                      {v === "yes" ? t.yes : t.no}
                    </label>
                  ))}
                </div>
                {formAdditionalDriver === "yes" && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.additionalDriverCountLabel}</label>
                    <input
                      type="number"
                      min="1"
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                      value={formAdditionalDriverCount}
                      onChange={e => setFormAdditionalDriverCount(e.target.value)}
                      placeholder={t.additionalDriverCountPlaceholder}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Firmenwagen */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.companyCarLabel}</label>
            <div style={{ display: "flex", gap: 12 }}>
              {["yes", "no"].map(v => (
                <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formCompanyCar === v ? "#4527a0" : "#ddd"}`, backgroundColor: formCompanyCar === v ? "#ede7f6" : "#fff" }}>
                  <input type="radio" name="companyCar" value={v} checked={formCompanyCar === v} onChange={() => setFormCompanyCar(v)} style={{ accentColor: "#4527a0" }} />
                  {v === "yes" ? t.yes : t.no}
                </label>
              ))}
            </div>
          </div>

          {formCompanyCar === "yes" && (
            <div style={{ padding: 16, backgroundColor: "#ede7f6", borderRadius: 8, marginBottom: 16, border: "1px solid #ce93d8" }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.companyCarPlateLabel}</label>
                <input
                  type="text"
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                  value={formCompanyCarPlate}
                  onChange={e => setFormCompanyCarPlate(e.target.value)}
                  placeholder={t.companyCarPlatePlaceholder}
                />
              </div>
              <div>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: 8 }}>{t.companyCarParkingLabel}</label>
                <div style={{ display: "flex", gap: 12 }}>
                  {["yes", "no"].map(v => (
                    <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 16px", borderRadius: 6, border: `2px solid ${formCompanyCarParking === v ? "#2e7d32" : "#ddd"}`, backgroundColor: formCompanyCarParking === v ? "#e8f5e9" : "#fff" }}>
                      <input type="radio" name="companyCarParking" value={v} checked={formCompanyCarParking === v} onChange={() => setFormCompanyCarParking(v)} style={{ accentColor: "#2e7d32" }} />
                      {v === "yes" ? t.yes : t.no}
                    </label>
                  ))}
                </div>
                {formCompanyCarParking === "yes" && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>{t.companyCarParkingLocationLabel}</label>
                    <input
                      type="text"
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                      value={formCompanyCarParkingLocation}
                      onChange={e => setFormCompanyCarParkingLocation(e.target.value)}
                      placeholder={t.companyCarParkingLocationPlaceholder}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={buildPlanFromForm}
            style={{ width: "100%", padding: "12px 0", backgroundColor: "#1565c0", color: "#fff", border: "none", borderRadius: 8, fontSize: 16, fontWeight: "bold", cursor: "pointer" }}
          >
            {t.createPlan}
          </button>
        </div>
      )}

      {/* Finale Zusammenfassung */}
      {finalPlan && finalPlan.requirements && (
        <div style={{ border: "2px solid #4caf50", padding: 15, borderRadius: 8 }}>
          <h2 style={{ color: "#4caf50" }}>{t.summaryTitle}</h2>
          
          {finalPlan.summary && (
            <p style={{ fontStyle: "italic", marginBottom: 15, padding: 10, backgroundColor: "#f1f8e9", borderRadius: 4 }}>
              {finalPlan.summary}
            </p>
          )}

          {/* Anforderungen anzeigen */}
          <div style={{ marginBottom: 20, padding: 15, backgroundColor: "#f5f5f5", borderRadius: 8 }}>
            <h3 style={{ marginTop: 0 }}>{t.details}</h3>
            {finalPlan.requirements.from && finalPlan.requirements.to && (
              <div style={{ marginBottom: 8 }}>
                <strong>{t.route}</strong> {finalPlan.requirements.from} → {finalPlan.requirements.to}
              </div>
            )}
            {finalPlan.requirements.dates && (
              <div style={{ marginBottom: 8 }}>
                <strong>{t.period}</strong> {finalPlan.requirements.dates.checkin}
                {finalPlan.requirements.dates.checkout
                  ? <> {t.periodTo} {finalPlan.requirements.dates.checkout}</>
                  : <> ({t.tripEndOpen})</>}
              </div>
            )}
            {finalPlan.requirements.onSite && (finalPlan.requirements.onSite.from || finalPlan.requirements.onSite.to) && (
              <div style={{ marginBottom: 8 }}>
                <strong>{t.onSitePeriodLabel}</strong> {finalPlan.requirements.onSite.from || "?"}
                {finalPlan.requirements.onSite.to && <> {t.periodTo} {finalPlan.requirements.onSite.to}</>}
              </div>
            )}
            {finalPlan.requirements.flight && (
              <div style={{ marginBottom: 8 }}>
                <strong>{t.flightLabel}</strong> {finalPlan.requirements.flight.needed ? (
                  <>
                    ✅ {t.yes}
                    {finalPlan.requirements.flight.luggage && <> - {finalPlan.requirements.flight.luggage}</>}
                    {(finalPlan.requirements.flight.outboundEarliestDeparture || finalPlan.requirements.flight.outboundTime) && (
                      <div style={{ marginLeft: 20, fontSize: 14, color: "#666" }}>
                        {t.outbound} {finalPlan.requirements.flight.outboundEarliestDeparture || finalPlan.requirements.flight.outboundTime}
                      </div>
                    )}
                    {(finalPlan.requirements.flight.returnEarliestDeparture || finalPlan.requirements.flight.returnTime) && (
                      <div style={{ marginLeft: 20, fontSize: 14, color: "#666" }}>
                        {t.returnFlight} {finalPlan.requirements.flight.returnEarliestDeparture || finalPlan.requirements.flight.returnTime}
                      </div>
                    )}
                  </>
                ) : `❌ ${t.no}`}
              </div>
            )}
            {finalPlan.requirements.accommodation && (
              <div style={{ marginBottom: 8 }}>
                <strong>{t.accommodationLabel}</strong> {finalPlan.requirements.accommodation.needed ? `✅ ${t.yes}` : `❌ ${t.no}`}
                {finalPlan.requirements.accommodation.needed && finalPlan.requirements.accommodation.checkIn && (
                  <div style={{ marginLeft: 20, fontSize: 14, color: "#666" }}>
                    {t.hotelPeriodLabel} {finalPlan.requirements.accommodation.checkIn}
                    {finalPlan.requirements.accommodation.checkOut && <> {t.periodTo} {finalPlan.requirements.accommodation.checkOut}</>}
                  </div>
                )}
              </div>
            )}
            {finalPlan.requirements.carRental && (
              <div style={{ marginBottom: 8 }}>
                <strong>{t.carRentalLabel}</strong> {finalPlan.requirements.carRental.needed ? `✅ ${t.yes}` : `❌ ${t.no}`}
              </div>
            )}
          </div>

          {/* Such-Links */}
          <div style={{ marginBottom: 20, padding: 15, backgroundColor: "#e3f2fd", borderRadius: 8 }}>
            <h3 style={{ marginTop: 0 }}>{t.searchTitle}</h3>
            <p style={{ marginBottom: 15, fontSize: 14 }}>
              {t.searchDesc}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {finalPlan.requirements.accommodation?.needed && (
                <a
                  href={`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(finalPlan.requirements.to || '')}&checkin=${finalPlan.requirements.accommodation?.checkIn || finalPlan.requirements.dates?.checkin || ''}&checkout=${finalPlan.requirements.accommodation?.checkOut || finalPlan.requirements.dates?.checkout || ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    padding: 12, 
                    backgroundColor: "#fff", 
                    borderRadius: 4, 
                    textDecoration: "none",
                    color: "#003580",
                    fontWeight: "bold",
                    border: "2px solid #003580"
                  }}
                >
                  {t.searchHotels}
                </a>
              )}
              {finalPlan.requirements.flight?.needed && (
                <a
                  href={`https://flug.idealo.de/flugroute/${encodeURIComponent(finalPlan.requirements.from || '')}/${encodeURIComponent(finalPlan.requirements.to || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    padding: 12, 
                    backgroundColor: "#fff", 
                    borderRadius: 4, 
                    textDecoration: "none",
                    color: "#ff6600",
                    fontWeight: "bold",
                    border: "2px solid #ff6600"
                  }}
                >
                  {t.searchFlights}
                </a>
              )}

            </div>
          </div>

          {/* Projektnummer (optional, auch nachträglich editierbar) */}
          <div style={{ marginBottom: 20 }}>
            <h3>{t.projectNumberLabel}</h3>
            <p style={{ fontSize: 14, color: "#666", marginBottom: 10 }}>
              {t.projectNumberHint}
            </p>
            <input
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
              value={projectNumber}
              onChange={e => setProjectNumber(e.target.value)}
              placeholder={t.projectNumberPlaceholder}
            />
          </div>

          {/* Benutzer-Notizen */}
          <div style={{ marginBottom: 20 }}>
            <h3>{t.notesTitle}</h3>
            <p style={{ fontSize: 14, color: "#666", marginBottom: 10 }}>
              {t.notesDesc}
            </p>
            <textarea
              style={{ 
                width: "100%", 
                minHeight: 120,
                padding: 10,
                borderRadius: 4,
                border: "2px solid #ddd",
                fontSize: 14,
                fontFamily: "inherit"
              }}
              value={userNotes}
              onChange={e => setUserNotes(e.target.value)}
              placeholder={t.notesPlaceholder}
            />
          </div>

          {/* Senden-Button — abhängig vom Modus */}
          {!requestSent && (
            <div style={{ marginTop: 20, paddingTop: 15, borderTop: "2px solid #ddd" }}>
              <button
                onClick={draftMode === "compose" ? sendDrafts : draftMode?.id ? submitDraft : editingRequestId ? saveEditedRequest : submitRequest}
                style={{
                  width: "100%",
                  padding: 15,
                  backgroundColor: draftMode === "compose" ? "#1565c0" : "#4caf50",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: "bold",
                  cursor: "pointer"
                }}
              >
                {draftMode === "compose" ? t.sendToEmployeesBtn : editingRequestId ? t.editSubmitBtn : t.submitBtn}
              </button>
              {draftMode !== "compose" && (
                <p style={{ fontSize: 12, color: "#666", marginTop: 10, textAlign: "center" }}>
                  {editingRequestId ? t.editSubmitHint : t.submitHint}
                </p>
              )}
            </div>
          )}
          
          {requestSent && (
            <div style={{ 
              marginTop: 20, 
              padding: 15, 
              backgroundColor: "#e8f5e9", 
              borderRadius: 8,
              borderLeft: "4px solid #4caf50"
            }}>
              <strong style={{ color: "#4caf50" }}>{t.submitted}</strong>
              <p style={{ margin: "5px 0 12px 0", fontSize: 14 }}>
                {t.adminNote}
              </p>
              <button
                onClick={resetForm}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#1565c0",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: "bold",
                  cursor: "pointer"
                }}
              >
                {t.newRequest}
              </button>
            </div>
          )}
        </div>
      )}

          </div>
        )}
      </div>

      {/* Bild-Lightbox: großes Bild als In-App-Dialog, responsiv (auch auf dem Handy) */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{ position: "fixed", inset: 0, zIndex: 1000, backgroundColor: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <button
            onClick={() => setLightboxSrc(null)}
            aria-label={t.close || "×"}
            style={{ position: "absolute", top: 12, right: 16, width: 40, height: 40, borderRadius: "50%", border: "none", backgroundColor: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 24, lineHeight: "40px", cursor: "pointer" }}
          >
            ×
          </button>
          <img
            src={lightboxSrc}
            alt=""
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 6, boxShadow: "0 4px 24px rgba(0,0,0,0.5)" }}
          />
        </div>
      )}
    </div>
  );
}

export default App;